import React, { useState, useRef, useEffect } from 'react';
import './ClientScriptPanel.css';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Bot, PenTool, Star, Lightbulb } from 'lucide-react';
import { getCurrentTrendingVideos } from '../setupTrendingVideos';
import { 
  getBodyLineCount, 
  getWordRange, 
  getPerBodyTime, 
  getPerLineWordRange, 
  countWords, 
  snapDuration 
} from '../utils/scriptHelpers';

const initialScriptLines = [
  { type: 'hook', text: '', notes: '', example: null },
  { type: 'line', text: '', notes: '', example: null },
  { type: 'cta', text: '', notes: '', example: null },
];

const scriptLoadingSteps = [
  "Analyzing your brief and settings...",
  "Understanding your campaign goals...",
  "Crafting engaging hooks...",
  "Building compelling body content...",
  "Creating effective CTAs...",
  "Polishing the final script...",
  "Almost ready!"
];

export default function ClientScriptPanel({ 
  open, 
  onClose, 
  script = null, 
  onScriptSaved, 
  initialCampaign, 
  initialBrief = null,
  forceMode = null, // 'ai' or 'manual' - bypasses step 1 if set
  startStep = 1, // Start at a specific step
  newScriptName: externalScriptName = null,
  setNewScriptName: setExternalScriptName = null
}) {
  const [step, setStep] = useState(startStep); // 1: Creation Method, 2: Name Script, 3: Brief Selection, 4: Script Settings, 5: Script Editor
  const [mode, setMode] = useState(forceMode || ''); // 'ai' or 'manual' - will be set in step 1 or forced
  const previousStepRef = useRef(startStep); // Track previous step to know navigation direction
  const [internalScriptName, setInternalScriptName] = useState('');
  const newScriptName = externalScriptName !== null ? externalScriptName : internalScriptName;
  const setNewScriptName = setExternalScriptName || setInternalScriptName;

  const [selectedCampaign, setSelectedCampaign] = useState(initialCampaign || '');
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [briefsList, setBriefsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hookRatings, setHookRatings] = useState({}); // Store hook ratings by line index
  const [ratingHookIndex, setRatingHookIndex] = useState(null); // Which hook is being rated
  const [isCreatingNewScript, setIsCreatingNewScript] = useState(true); // Track if creating new script

  // Script settings state
  const [scriptSettings, setScriptSettings] = useState({
    platform: '',
    creativeControl: 'exact', // 'exact', 'some', 'full'
    videoLength: '30', // in seconds
    videoFormat: '9:16',
    additionalHooks: 0,
    additionalCTAs: 0,
    productType: 'product', // 'product' or 'service'
    deliveryTime: '' // delivery time in days for products, or 'immediate' for services
  });

  const [aiScriptStyle, setAiScriptStyle] = useState('trend');
  const [scriptLibrary, setScriptLibrary] = useState([{ id: 1, name: 'Script 1', lines: initialScriptLines }]);
  const [activeScriptIdx, setActiveScriptIdx] = useState(0);
  const [bodyLines, setBodyLines] = useState([{ text: '', action: '', notes: '' }]);
  const [extraHooksCount, setExtraHooksCount] = useState(0);
  const [extraCTAsCount, setExtraCTAsCount] = useState(0);
  
  // Debug step changes - must be after all state declarations
  useEffect(() => {
    const previousStep = previousStepRef.current;
    const currentScriptLibraryLength = scriptLibrary.length;
    console.log('Step changed to:', step, 'from:', previousStep, 'mode:', mode, 'scriptLibrary.length:', currentScriptLibraryLength, 'isCreatingNewScript:', isCreatingNewScript);
    
    // Only clear script data when going to step 4 from step 3 (brief selection), not from step 5 (editor)
    // This preserves the script when navigating back from editor to settings
    if (step === 4 && mode === 'manual' && previousStep === 3) {
      console.log('Step 4 manual mode: Coming from step 3, clearing script library and hook ratings');
      setScriptLibrary([]);
      setActiveScriptIdx(0);
      setHookRatings({});
      setRatingHookIndex(null);
      setIsCreatingNewScript(true);
    }
    
    // When coming back from step 5 (editor) to step 4 (settings), preserve the script state
    // If we're coming back from the editor, we must have a script, so preserve isCreatingNewScript = false
    if (step === 4 && previousStep === 5) {
      console.log('Step 4: Coming back from editor, preserving script state. Current scriptLibrary.length:', currentScriptLibraryLength);
      // Only preserve if we actually have a script
      if (currentScriptLibraryLength > 0) {
        setIsCreatingNewScript(false);
        console.log('Preserved: isCreatingNewScript set to false');
      } else {
        console.warn('Warning: Coming back from editor but scriptLibrary is empty!');
      }
    }
    
    // Update previous step ref
    previousStepRef.current = step;
  }, [step, mode]);
  
  // Trending videos modal state
  const [showTrendingModal, setShowTrendingModal] = useState(false);
  const [selectedTrendingVideo, setSelectedTrendingVideo] = useState(null);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [loadingTrendingVideos, setLoadingTrendingVideos] = useState(true);
  const [regeneratingLineIndex, setRegeneratingLineIndex] = useState(null);
  const [regeneratingLineType, setRegeneratingLineType] = useState(null);
  const panelRef = useRef(null);
  const responseTopRef = useRef(null);
  const scriptCount = scriptLibrary.length;

  const isManualCreativeFlair = mode === 'manual' && scriptSettings.creativeControl === 'some';

  // Style-specific creative flair guidance maps (detailed script flow format)
  const creativeFlairGuidanceByStyle = {
    trend: {
      hook: 'Start with a trending hook format—use a popular question, challenge, or "POV" style that\'s currently viral. Reference current trends, sounds, or formats that are performing well right now. Create FOMO by hinting at a trending result or transformation that viewers want.',
      line: 'Use trending transitions, cuts, or effects that match current platform trends. Incorporate trending audio, sounds, or music that enhances the message. Show the product in a way that feels fresh and aligned with what\'s working now on the platform. Keep the energy high and match the pacing of trending content.',
      cta: 'End with a trending CTA format—use popular phrases like "Try it and let me know", "Save this", or "Follow for more". Add trending emojis or gestures that are currently popular. Create shareability by making the CTA feel like part of a trending conversation.'
    },
    competitive: {
      hook: 'Start with a real-world problem or relatable need—show an authentic moment where the product becomes relevant. Use genuine, conversational tone that builds trust and feels authentic. Create connection by addressing a common pain point viewers can relate to.',
      line: 'Demonstrate the product in natural, everyday settings with honest reactions and genuine feedback. Show it being used in real situations with clear, practical demonstrations. Highlight specific features through authentic use cases. Keep it relatable and trustworthy.',
      cta: 'End with a clear value proposition and genuine call-to-action. Show real results and authentic outcomes. Use confident but honest language that builds trust and makes viewers feel confident in their choice.'
    },
    viral: {
      hook: 'Start with a hook that creates immediate curiosity, shock, or relatability—something shareable. Use patterns that go viral: unexpected reveals, relatable problems, or "wait until you see" moments. Create an emotional hook that makes viewers want to share it with others immediately.',
      line: 'Build momentum with unexpected moments, reveals, or transformations that keep viewers engaged throughout. Use storytelling techniques that create shareable moments—before/after, problem/solution, transformation. Layer in elements that make people want to comment, share, or tag someone. Keep the narrative compelling and surprising.',
      cta: 'End with a CTA that encourages sharing—"Tag someone who needs this", "Save this for later", or "Share if you agree". Create a moment that makes viewers want to engage—ask a question, create a debate, or invite participation. Use language that makes the CTA feel like part of a viral conversation or trend.'
    }
  };

  // Parse trending video stylePrompt to create detailed script flow instructions
  const parseTrendingVideoStylePrompt = (stylePrompt) => {
    if (!stylePrompt) return null;

    const flowInstructions = {
      hook: '',
      line: '',
      cta: ''
    };

    // Extract and combine all relevant sections for HOOK
    const hookMatch = stylePrompt.match(/HOOK PATTERN:([\s\S]*?)(?=BODY CONTENT:|CALL-TO-ACTION:|VISUAL STYLE:|TONE|PACING|$)/i);
    const toneMatch = stylePrompt.match(/TONE & DELIVERY:([\s\S]*?)(?=HOOK PATTERN:|BODY CONTENT:|CALL-TO-ACTION:|VISUAL STYLE:|PACING|$)/i);
    
    let hookFlow = '';
    if (hookMatch) {
      const hookContent = hookMatch[1].trim();
      // Extract examples in quotes first
      const quotedExamples = hookContent.match(/"([^"]+)"/g) || hookContent.match(/'([^']+)'/g);
      if (quotedExamples && quotedExamples.length > 0) {
        hookFlow = `Start with a hook that follows this pattern: ${quotedExamples.map(e => e.replace(/["']/g, '')).join(' or ')}. `;
      }
      // Extract bullet points and combine into flowing text
      const hookBullets = hookContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').trim())
        .filter(line => line.length > 10 && !line.match(/^["']/));
      
      if (hookBullets.length > 0) {
        hookFlow += hookBullets.join(' Then, ').toLowerCase() + '.';
      } else if (!hookFlow) {
        // Use the whole hook content as guidance
        hookFlow = hookContent.replace(/^[-*•\s]+/gm, '').trim();
      }
    }
    
    if (toneMatch) {
      const toneContent = toneMatch[1].trim();
      const tonePoints = toneContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').trim())
        .filter(line => line.length > 10);
      
      if (tonePoints.length > 0) {
        hookFlow += (hookFlow ? ' ' : '') + `Deliver this with ${tonePoints[0].toLowerCase()}.`;
      }
    }
    
    flowInstructions.hook = hookFlow || 'Follow the hook pattern and tone from the trending video style.';

    // Extract and combine all relevant sections for BODY
    const bodyMatch = stylePrompt.match(/BODY CONTENT:([\s\S]*?)(?=CALL-TO-ACTION:|VISUAL STYLE:|TONE|PACING|$)/i);
    const visualMatch = stylePrompt.match(/VISUAL STYLE:([\s\S]*?)(?=TONE|HOOK PATTERN:|BODY CONTENT:|CALL-TO-ACTION:|PACING|$)/i);
    const pacingMatch = stylePrompt.match(/PACING:([\s\S]*?)(?=VISUAL STYLE:|TONE|HOOK PATTERN:|BODY CONTENT:|CALL-TO-ACTION:|$)/i);
    
    let bodyFlow = '';
    if (bodyMatch) {
      const bodyContent = bodyMatch[1].trim();
      const bodyBullets = bodyContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').trim())
        .filter(line => line.length > 10);
      
      if (bodyBullets.length > 0) {
        bodyFlow = bodyBullets.join(' Next, ').toLowerCase() + '.';
      } else {
        bodyFlow = bodyContent.replace(/^[-*•\s]+/gm, '').trim();
      }
    }
    
    if (visualMatch) {
      const visualContent = visualMatch[1].trim();
      const visualPoints = visualContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').trim())
        .filter(line => line.length > 10);
      
      if (visualPoints.length > 0) {
        bodyFlow += (bodyFlow ? ' Visually, ' : '') + visualPoints.join(', ').toLowerCase() + '.';
      }
    }
    
    if (pacingMatch) {
      const pacingContent = pacingMatch[1].trim();
      const pacingPoints = pacingContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').trim())
        .filter(line => line.length > 10);
      
      if (pacingPoints.length > 0) {
        bodyFlow += (bodyFlow ? ' For pacing, ' : '') + pacingPoints[0].toLowerCase() + '.';
      }
    }
    
    flowInstructions.line = bodyFlow || 'Follow the body content, visual style, and pacing from the trending video.';

    // Extract and combine all relevant sections for CTA
    const ctaMatch = stylePrompt.match(/CALL-TO-ACTION:([\s\S]*?)(?=VISUAL STYLE:|TONE|PACING|$)/i);
    
    let ctaFlow = '';
    if (ctaMatch) {
      const ctaContent = ctaMatch[1].trim();
      // Extract examples in quotes first
      const quotedExamples = ctaContent.match(/"([^"]+)"/g) || ctaContent.match(/'([^']+)'/g);
      if (quotedExamples && quotedExamples.length > 0) {
        ctaFlow = `End with a call-to-action like: ${quotedExamples.map(e => e.replace(/["']/g, '')).join(' or ')}. `;
      }
      // Extract bullet points
      const ctaBullets = ctaContent.split('\n')
        .map(line => line.trim())
        .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
        .map(line => line.replace(/^[-*•]\s*/, '').replace(/^["']|["']$/g, '').trim())
        .filter(line => line.length > 10 && !line.match(/^["']/));
      
      if (ctaBullets.length > 0) {
        ctaFlow += ctaBullets.join(' Make sure to ').toLowerCase() + '.';
      } else if (!ctaFlow) {
        ctaFlow = ctaContent.replace(/^[-*•\s]+/gm, '').trim();
      }
    }
    
    flowInstructions.cta = ctaFlow || 'Use the call-to-action style from the trending video.';

    return flowInstructions;
  };

  // Default/fallback creative flair guidance (for when style is not set) - detailed script flow format
  const creativeFlairGuidanceMap = {
    hook: 'Open with a quick personal moment, question, or reaction about the core problem. Use expressive body language or a visual prop to dramatize what you felt before finding the product. Hint at the outcome you are about to reveal to keep viewers watching.',
    line: 'Share a short, real-life moment that shows how the product fits into your routine. Layer in a sensory detail (what you felt, saw, or heard) to make it believable. Show the product in action while you talk—switch angles or settings to keep it dynamic and engaging.',
    cta: 'Restate the benefit in your own words before inviting viewers to take action. Point or look toward where they should tap/click while keeping the tone upbeat. Add urgency or exclusivity so they feel prompted to act now.'
  };

  useEffect(() => {
    if (!open) return;
    const resetScroll = () => {
      if (panelRef.current) {
        panelRef.current.scrollTop = 0;
      }
    };
    const raf = requestAnimationFrame(resetScroll);
    const timeout = setTimeout(resetScroll, 60);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step, mode]);

  useEffect(() => {
    if (!open) return;
    const resetScroll = () => {
      if (panelRef.current) {
        panelRef.current.scrollTop = 0;
      }
    };
    const raf = requestAnimationFrame(resetScroll);
    const timeout = setTimeout(resetScroll, 60);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step, mode]);

  useEffect(() => {
    if (!open || step !== 5) return;

    const scrollToResponse = () => {
      if (panelRef.current) {
        panelRef.current.scrollTop = 0;
      }
      if (responseTopRef.current) {
        responseTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const raf = requestAnimationFrame(scrollToResponse);
    const timeout = setTimeout(scrollToResponse, 100);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step, scriptCount, activeScriptIdx, isCreatingNewScript]);

  const platforms = [
    { value: 'meta', label: 'Meta (Facebook/Instagram)', icon: '/social%20icons/meta.svg', type: 'image' },
    { value: 'tiktok', label: 'TikTok', icon: '/social%20icons/Tiktok.svg', type: 'image' },
    { value: 'youtube', label: 'YouTube', icon: '/social%20icons/Youtube.svg', type: 'image' }
  ];

  const creativeControlOptions = [
    { value: 'exact', label: 'Follow script exactly', icon: '📌', description: 'Creators must follow the script word for word', type: 'emoji' },
    { value: 'some', label: 'Add creative flair', icon: '🎨', description: 'Creators can add their style while keeping key points', type: 'emoji' }
  ];

  const videoLengths = [
    { value: '30', label: '30 seconds', icon: '⏱️' },
    { value: '60', label: '1 minute', icon: '⏲️' },
    { value: '90', label: '1:30', icon: '⏳' }
  ];

  const videoFormats = [
    { value: '9:16', label: '9:16 Vertical', icon: '📱' },
    { value: '16:9', label: '16:9 Landscape', icon: '🖥️' },
    { value: '1:1', label: '1:1 Square', icon: '🔲' }
  ];

  const productTypes = [
    { value: 'product', label: 'Physical Product', icon: '📦', description: 'Items need to be shipped to creators' },
    { value: 'service', label: 'Service', icon: '🎯', description: 'Digital service creators can start immediately' }
  ];

  const getCreativeGuidance = (line) => {
    if (mode !== 'ai') return null;
    if (scriptSettings.creativeControl !== 'some') return null;
    
    // If "Take Inspiration" is selected, use the trending video's stylePrompt
    if (isTakeInspiration && selectedTrendingVideo?.stylePrompt) {
      const trendingFlow = parseTrendingVideoStylePrompt(selectedTrendingVideo.stylePrompt);
      if (trendingFlow && trendingFlow[line.type]) {
        return { flow: trendingFlow[line.type] };
      }
    }
    
    // Otherwise, get style-specific guidance based on aiScriptStyle
    const styleGuidance = creativeFlairGuidanceByStyle[aiScriptStyle] || creativeFlairGuidanceMap;
    const flow = styleGuidance[line.type] || creativeFlairGuidanceMap[line.type];
    
    if (!flow) return null;
    return { flow };
  };

  const buildContextTips = (parsedLines = [], briefData = {}) => {
    const hooks = parsedLines.filter(l => l.type === 'hook');
    const bodyLines = parsedLines.filter(l => l.type === 'line');
    const ctas = parsedLines.filter(l => l.type === 'cta');
    const generatedHooks = hooks.map(h => h.text.trim()).filter(Boolean);
    const generatedBodyLines = bodyLines.map(l => l.text.trim()).filter(Boolean);
    const generatedCtas = ctas.map(c => c.text.trim()).filter(Boolean);

    const requirementList = Array.isArray(briefData.requirements)
      ? briefData.requirements.filter(Boolean)
      : (briefData.requirements ? [briefData.requirements] : []);

    return {
      briefData: briefData, // Pass brief data for story flow
      hook: [
        generatedHooks.length > 0
          ? `Use this core promise: "${generatedHooks[0]}". Deliver it in your own words without changing the promise.`
          : `Call out how ${briefData.product || 'this product'} flips a common pain point for ${briefData.audience || 'viewers like you'}.`,
        briefData.message
          ? `Mention what convinced you most: ${briefData.message}.`
          : 'Paint a quick before/after moment so viewers feel the shift.'
      ],
      line: [
        generatedBodyLines.length > 0
          ? `In your tone, show why "${generatedBodyLines[0]}" is true. Demonstrate or narrate the real moment it helped.`
          : `Describe the first time ${briefData.product || 'this'} worked for you—what happened step-by-step?`,
        ...requirementList.map(req => `Work in this detail: ${req}.`)
      ],
      cta: [
        generatedCtas.length > 0
          ? `Rephrase "${generatedCtas[0]}" with your personality so the call-to-action stays the same but sounds like you.`
          : `Tell viewers how to try ${briefData.product || 'it'} themselves in clear language.`,
        briefData.goal
          ? `Remind them this helps with ${briefData.goal} so the payoff is obvious.`
          : 'Add a friendly nudge—limited spots, your personal recommendation, or "before you forget".'
      ]
    };
  };

  // Generate notes - specific to the actual script line content
  const generateDetailedNotes = (lineType, briefData = {}, lineIndex = 0, totalBodyLines = 1, style = 'trend', scriptLength = 30, campaignGoal = '', scriptLine = '') => {
    if (!scriptLine || typeof scriptLine !== 'string' || scriptLine.trim().length === 0) {
      return 'Make it your own—feel free to use these examples or create your own variation.';
    }
    
    const angle = determineScriptAngle(style, briefData, campaignGoal);
    const productName = angle.productName;
    const problem = angle.problem;
    const primaryBenefit = angle.primaryBenefit;
    const audience = angle.audience;
    const keyBenefits = briefData.keyBenefits || briefData.contentGuidelines?.keyBenefits || [];
    
    const lowerScript = scriptLine.toLowerCase();
    let notes = [];
    
    // Analyze the specific script line to extract what it's about
    const mentionsProblem = problem && lowerScript.includes(problem.toLowerCase());
    const mentionsProduct = productName && lowerScript.includes(productName.toLowerCase());
    const mentionsBenefit = primaryBenefit && lowerScript.includes(primaryBenefit.toLowerCase());
    const isQuestion = scriptLine.trim().endsWith('?');
    const isPersonal = lowerScript.includes('i ') || lowerScript.includes('my ') || lowerScript.includes('me');
    const isDirective = lowerScript.includes('you ') || lowerScript.includes('your ') || lowerScript.includes('try ') || lowerScript.includes('get ');
    const mentionsResult = lowerScript.includes('result') || lowerScript.includes('changed') || lowerScript.includes('helped') || lowerScript.includes('got');
    const mentionsTried = lowerScript.includes('tried') || lowerScript.includes('used') || lowerScript.includes('discovered');
    
    // Generate line-specific notes based on what's actually in the script
    if (lineType === 'hook') {
      notes.push('Alternative ways to say this:');
      
      // Create variations based on the actual script line structure
      if (isQuestion) {
        // If it's a question, provide question variations
        if (mentionsProblem) {
          notes.push(`• "I was dealing with ${problem} until I found ${productName}"`);
          notes.push(`• "Anyone else struggling with ${problem}? ${productName} actually works"`);
        } else if (mentionsProduct) {
          notes.push(`• "I can't believe ${productName} actually delivers ${primaryBenefit}"`);
          notes.push(`• "Wait, ${productName} does WHAT?"`);
        } else {
          notes.push(`• "I discovered ${productName} and it changed everything"`);
          notes.push(`• "POV: you just found ${productName} that actually works"`);
        }
      } else if (isPersonal) {
        // If it's personal, provide more personal variations
        if (mentionsProblem) {
          notes.push(`• "I used to struggle with ${problem} until ${productName} changed that"`);
          notes.push(`• "My experience with ${problem} changed when I found ${productName}"`);
        } else {
          notes.push(`• "I tried ${productName} and here's what happened"`);
          notes.push(`• "My honest review of ${productName} after using it"`);
        }
      } else {
        // Generic hook - provide more specific variations
        if (mentionsProblem) {
          notes.push(`• "I was struggling with ${problem} until I discovered ${productName}"`);
          notes.push(`• "If you're dealing with ${problem}, ${productName} might help"`);
        } else if (mentionsProduct) {
          notes.push(`• "I tried ${productName} and it actually works"`);
          notes.push(`• "Let me show you what ${productName} does"`);
        } else {
          notes.push(`• "I discovered something that changed everything"`);
          notes.push(`• "POV: you find out about ${productName}"`);
        }
      }
      
      // Add specific guidance based on what the line mentions
      if (mentionsProblem) {
        notes.push(`\nWhat to focus on: Show the problem (${problem}) and how ${productName} solves it`);
      }
      if (mentionsProduct && !mentionsProblem) {
        notes.push(`\nWhat to focus on: Highlight ${productName} and what makes it special`);
      }
    } else if (lineType === 'line') {
      const isFirstBody = lineIndex === 0;
      const isLastBody = lineIndex === totalBodyLines - 1;
      
      notes.push('Alternative ways to say this:');
      
      // Create variations based on the actual script line content
      if (mentionsTried || mentionsResult) {
        // If it's about trying or results, provide similar variations
        if (mentionsProblem) {
          notes.push(`• "I tried ${productName} for ${problem} and the results surprised me"`);
          notes.push(`• "When I used ${productName} to solve ${problem}, here's what changed"`);
        } else if (mentionsBenefit) {
          notes.push(`• "I got ${primaryBenefit} after using ${productName}"`);
          notes.push(`• "The best part about ${productName}? It delivers ${primaryBenefit}"`);
        } else {
          notes.push(`• "My experience with ${productName} has been amazing"`);
          notes.push(`• "I've been using ${productName} and here's what I noticed"`);
        }
      } else if (isFirstBody) {
        // First body line - set up the story
        if (mentionsProblem) {
          notes.push(`• "I was dealing with ${problem} until I tried ${productName}"`);
          notes.push(`• "When ${problem} became an issue, ${productName} was the solution"`);
        } else {
          notes.push(`• "I discovered ${productName} and it's been a game-changer"`);
          notes.push(`• "Let me show you how ${productName} works"`);
        }
      } else if (isLastBody) {
        // Last body line - show the payoff
        if (mentionsResult || mentionsBenefit) {
          notes.push(`• "The result? ${primaryBenefit}${problem ? ` and no more ${problem}` : ''}"`);
          notes.push(`• "After using ${productName}, I finally got ${primaryBenefit}"`);
        } else {
          notes.push(`• "This is why ${productName} works for me"`);
          notes.push(`• "I can't imagine going back to life without ${productName}"`);
        }
      } else {
        // Middle body lines - continue the story
        if (mentionsProduct) {
          notes.push(`• "What I love about ${productName} is how it${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`}"`);
          notes.push(`• "Another thing about ${productName}..."`);
        } else {
          notes.push(`• "Let me show you more about how this works"`);
          notes.push(`• "Here's what else I noticed..."`);
        }
      }
      
      // Add specific guidance based on line position and content
      if (isFirstBody) {
        notes.push(`\nWhat to show: Introduce ${productName} and${problem ? ` the problem (${problem})` : ` the benefit (${primaryBenefit})`}`);
      } else if (isLastBody) {
        notes.push(`\nWhat to show: The transformation or result—${primaryBenefit}${problem ? ` and how ${problem} is solved` : ''}`);
      } else {
        notes.push(`\nWhat to show: Continue building the story—${mentionsProduct ? `show ${productName} in action` : 'add more detail'}`);
      }
    } else if (lineType === 'cta') {
      const goal = campaignGoal || briefData.goal || '';
      notes.push('Alternative ways to say this:');
      
      // Create variations based on the actual CTA content
      if (isDirective) {
        // If it's directive, provide similar directive variations
        if (mentionsProblem) {
          if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
            notes.push(`• "Try ${productName} if you're dealing with ${problem}—link in bio"`);
            notes.push(`• "Get ${productName} now to solve ${problem}—link in bio"`);
          } else if (goal && goal.toLowerCase().includes('engagement')) {
            notes.push(`• "Comment if ${productName} helped with ${problem}"`);
            notes.push(`• "Tag someone who deals with ${problem}"`);
          } else {
            notes.push(`• "Save this if you're dealing with ${problem}"`);
            notes.push(`• "Follow for more solutions to ${problem}"`);
          }
        } else if (mentionsProduct) {
          if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
            notes.push(`• "Get ${productName} now—link in bio"`);
            notes.push(`• "Shop ${productName} for ${primaryBenefit}—link in bio"`);
          } else if (goal && goal.toLowerCase().includes('engagement')) {
            notes.push(`• "Comment if you've tried ${productName}"`);
            notes.push(`• "Share this if ${productName} worked for you"`);
          } else {
            notes.push(`• "Save this for later"`);
            notes.push(`• "Follow for more on ${productName}"`);
          }
        } else {
          // Generic CTA
          if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
            notes.push(`• "Try ${productName} if you want ${primaryBenefit}—link in bio"`);
            notes.push(`• "Get ${productName} now—link in bio"`);
          } else {
            notes.push(`• "Comment your thoughts"`);
            notes.push(`• "Save this if it helped"`);
          }
        }
      } else {
        // Non-directive CTA
        if (mentionsProduct) {
          notes.push(`• "I highly recommend ${productName}${problem ? ` if you're dealing with ${problem}` : ''}"`);
          notes.push(`• "${productName} has been amazing for me"`);
        } else {
          notes.push(`• "This has been a game-changer for me"`);
          notes.push(`• "I can't recommend this enough"`);
        }
      }
      
      // Add specific guidance
      if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
        notes.push(`\nWhat to show: Clear call-to-action—how to get ${productName}`);
      } else if (goal && goal.toLowerCase().includes('engagement')) {
        notes.push(`\nWhat to show: Invite interaction—encourage comments, tags, or shares`);
      } else {
        notes.push(`\nWhat to show: Next step for ${audience}—what they should do`);
      }
    }
    
    // Add key benefits if relevant
    if (keyBenefits.length > 0 && (mentionsProduct || mentionsBenefit)) {
      const relevantBenefits = keyBenefits.filter(b => b && b !== primaryBenefit).slice(0, 2);
      if (relevantBenefits.length > 0) {
        notes.push(`\nBenefits you could mention: ${relevantBenefits.join(', ')}`);
      }
    }
    
    return notes.length > 0 ? notes.join('\n') : 'Make it your own—feel free to use these examples or create your own variation.';
  };

  // Helper: Determine script detail level based on length
  const getScriptDetailLevel = (scriptLength) => {
    if (scriptLength <= 15) return 'concise'; // Fast-paced, essential points only
    if (scriptLength <= 30) return 'detailed'; // More story, more detail
    return 'comprehensive'; // Full story arc, lots of detail
  };

  // Helper: Get pacing guidance based on length and style
  const getPacingGuidance = (scriptLength, style) => {
    if (scriptLength <= 15) {
      return style === 'trend' || style === 'trending' ? 'Keep it fast—quick cuts, rapid delivery, high energy' :
             style === 'viral' ? 'Ultra-fast pacing—make every second count' :
             'Keep it concise but authentic—no wasted words';
    }
    if (scriptLength <= 30) {
      return style === 'trend' || style === 'trending' ? 'Moderate pace—build momentum with trending elements' :
             style === 'viral' ? 'Dynamic pacing—mix fast and slow moments for impact' :
             'Natural pacing—let the story breathe but stay engaging';
    }
    return style === 'trend' || style === 'trending' ? 'Varied pacing—build from discovery to payoff' :
           style === 'viral' ? 'Strategic pacing—create peaks and valleys' :
           'Thoughtful pacing—take time to build trust and demonstrate value';
  };

  // Clean and normalize text from brief data
  const cleanBriefText = (text, maxLength = 50) => {
    if (!text || typeof text !== 'string') return '';
    
    // Remove extra whitespace and normalize
    let cleaned = text.trim().replace(/\s+/g, ' ');
    
    // Extract product name if it's a long description
    // Look for patterns like "called [name]" or "named [name]" or just use first few words if it's very long
    if (cleaned.length > maxLength) {
      const calledMatch = cleaned.match(/(?:called|named|titled)\s+([A-Z][a-zA-Z0-9\s]{1,30})/i);
      if (calledMatch && calledMatch[1]) {
        cleaned = calledMatch[1].trim();
      } else {
        // Take first sentence or first maxLength characters
        const firstSentence = cleaned.match(/^[^.!?]+[.!?]?/)?.[0] || cleaned.substring(0, maxLength);
        cleaned = firstSentence.trim();
        if (cleaned.length > maxLength) {
          cleaned = cleaned.substring(0, maxLength).trim();
        }
      }
    }
    
    // Remove repetitive phrases
    const words = cleaned.split(' ');
    const seen = new Set();
    const uniqueWords = [];
    for (let i = 0; i < words.length; i++) {
      const word = words[i].toLowerCase();
      if (!seen.has(word) || uniqueWords.length < 5) {
        seen.add(word);
        uniqueWords.push(words[i]);
      }
    }
    cleaned = uniqueWords.join(' ');
    
    // Limit final length
    if (cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength).trim();
      // Remove trailing incomplete words
      cleaned = cleaned.replace(/\s+\w+$/, '');
    }
    
    return cleaned;
  };

  // Determine the script angle/vision based on brief data and campaign goal
  const determineScriptAngle = (style, briefData = {}, campaignGoal = '') => {
    const product = briefData.product || 'the product';
    // Clean product name - extract just the name if it's a long description
    let productName = product !== 'the product' ? cleanBriefText(product, 30) : '[product]';
    if (!productName || productName === '') productName = '[product]';
    
    const problem = briefData.problem 
      || briefData.contentGuidelines?.problem 
      || briefData.manualBriefSections?.problem
      || (briefData.manualBriefSections?.contentGuidelines && typeof briefData.manualBriefSections.contentGuidelines === 'string' 
          ? briefData.manualBriefSections.contentGuidelines.match(/problem[:\s]+([^\n]+)/i)?.[1]?.trim() 
          : '');
    const cleanedProblem = cleanBriefText(problem, 40);
    
    const keyBenefits = briefData.keyBenefits || briefData.contentGuidelines?.keyBenefits || [];
    // Use campaign goal if available, otherwise fall back to brief goal
    const goal = campaignGoal || briefData.goal || '';
    const audience = briefData.audience || '';
    const cleanedAudience = cleanBriefText(audience, 25);
    const message = briefData.message || '';
    
    // Format goal text
    let goalPhrase = '';
    if (goal) {
      const goalLower = goal.toLowerCase().trim();
      if (goalLower.match(/^(sales|conversions|views|engagement|leads|signups)$/)) {
        goalPhrase = `to get ${goalLower}`;
      } else if (goalLower.startsWith('to ')) {
        goalPhrase = goalLower;
      } else if (goalLower.startsWith('increase ') || goalLower.startsWith('improve ') || goalLower.startsWith('boost ')) {
        goalPhrase = goalLower;
      } else {
        goalPhrase = `to ${goalLower}`;
      }
    }
    
    // Clean benefits and message
    const primaryBenefit = keyBenefits && keyBenefits.length > 0 
      ? cleanBriefText(keyBenefits[0], 30) 
      : (message ? cleanBriefText(message, 30) : 'the key benefit');
    const secondaryBenefit = keyBenefits && keyBenefits.length > 1 
      ? cleanBriefText(keyBenefits[1], 30) 
      : '';
    
    // Determine the core angle based on style and brief
    let angle = {
      coreMessage: '',
      hookFocus: '',
      bodyFocus: '',
      payoffFocus: '',
      ctaFocus: '',
      targetAudience: cleanedAudience || 'viewers',
      transformation: ''
    };
    
    if (style === 'trend' || style === 'trending') {
      angle.coreMessage = cleanedProblem 
        ? `${productName} solves ${cleanedProblem} and delivers ${primaryBenefit}`
        : `${productName} delivers ${primaryBenefit}`;
      angle.hookFocus = cleanedProblem 
        ? `discovery moment—finding ${productName} that solves ${cleanedProblem}`
        : `discovery moment—finding ${productName} that delivers ${primaryBenefit}`;
      angle.bodyFocus = cleanedProblem
        ? `show how ${productName} addresses ${cleanedProblem} and delivers ${primaryBenefit}${secondaryBenefit ? `, plus ${secondaryBenefit}` : ''}`
        : `show how ${productName} delivers ${primaryBenefit}${secondaryBenefit ? ` and ${secondaryBenefit}` : ''}`;
      angle.payoffFocus = `the transformation: ${primaryBenefit}${cleanedProblem ? ` by solving ${cleanedProblem}` : ''}`;
      // CTA focus based on campaign goal
      if (goalPhrase) {
        if (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions')) {
          angle.ctaFocus = `drive action—invite ${cleanedAudience || 'viewers'} to try ${productName} and make a purchase`;
        } else if (goal.toLowerCase().includes('engagement') || goal.toLowerCase().includes('community')) {
          angle.ctaFocus = `encourage engagement—invite ${cleanedAudience || 'viewers'} to comment, share, or interact with your content about ${productName}`;
        } else if (goal.toLowerCase().includes('views') || goal.toLowerCase().includes('reach')) {
          angle.ctaFocus = `maximize reach—invite ${cleanedAudience || 'viewers'} to follow, save, or share this content about ${productName}`;
        } else {
          angle.ctaFocus = `invite ${cleanedAudience || 'viewers'} to try ${productName} ${goalPhrase}`;
        }
      } else {
        angle.ctaFocus = `invite ${cleanedAudience || 'viewers'} to experience ${primaryBenefit}`;
      }
      angle.transformation = cleanedProblem 
        ? `from struggling with ${cleanedProblem} to experiencing ${primaryBenefit}`
        : `to experiencing ${primaryBenefit}`;
    } else if (style === 'competitive') {
      angle.coreMessage = cleanedProblem
        ? `${productName} is the authentic solution for ${cleanedProblem}, delivering ${primaryBenefit}`
        : `${productName} delivers ${primaryBenefit} through authentic, proven results`;
      angle.hookFocus = cleanedProblem
        ? `relatable problem—${cleanedAudience || 'people'} dealing with ${cleanedProblem}`
        : `real need—${cleanedAudience || 'people'} who need ${primaryBenefit}`;
      angle.bodyFocus = cleanedProblem
        ? `demonstrate how ${productName} solves ${cleanedProblem} in real situations, showing ${primaryBenefit}${secondaryBenefit ? ` and ${secondaryBenefit}` : ''}`
        : `demonstrate how ${productName} delivers ${primaryBenefit}${secondaryBenefit ? ` and ${secondaryBenefit}` : ''} in authentic scenarios`;
      angle.payoffFocus = `the proof: ${primaryBenefit}${cleanedProblem ? ` by solving ${cleanedProblem}` : ''}—real results that work`;
      // CTA focus based on campaign goal
      if (goalPhrase) {
        if (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions')) {
          angle.ctaFocus = cleanedProblem
            ? `drive sales—recommend ${productName} to ${cleanedAudience || 'anyone'} dealing with ${cleanedProblem} and encourage them to purchase`
            : `drive sales—recommend ${productName} to ${cleanedAudience || 'anyone'} who needs ${primaryBenefit} and encourage them to purchase`;
        } else if (goal.toLowerCase().includes('engagement') || goal.toLowerCase().includes('community')) {
          angle.ctaFocus = cleanedProblem
            ? `build engagement—recommend ${productName} to ${cleanedAudience || 'anyone'} dealing with ${cleanedProblem} and invite them to share their experience`
            : `build engagement—recommend ${productName} to ${cleanedAudience || 'anyone'} who needs ${primaryBenefit} and invite them to share their experience`;
        } else {
          angle.ctaFocus = cleanedProblem
            ? `recommend ${productName} to ${cleanedAudience || 'anyone'} dealing with ${cleanedProblem} who wants ${primaryBenefit}`
            : `recommend ${productName} to ${cleanedAudience || 'anyone'} who needs ${primaryBenefit}`;
        }
      } else {
        angle.ctaFocus = cleanedProblem
          ? `recommend ${productName} to ${cleanedAudience || 'anyone'} dealing with ${cleanedProblem} who wants ${primaryBenefit}`
          : `recommend ${productName} to ${cleanedAudience || 'anyone'} who needs ${primaryBenefit}`;
      }
      angle.transformation = cleanedProblem
        ? `from ${cleanedProblem} to ${primaryBenefit}`
        : `to ${primaryBenefit}`;
    } else if (style === 'viral') {
      angle.coreMessage = cleanedProblem
        ? `${productName} does something unexpected: it solves ${cleanedProblem} and delivers ${primaryBenefit}`
        : `${productName} delivers ${primaryBenefit} in a surprising, shareable way`;
      angle.hookFocus = `unexpected reveal—${productName} does something viewers don't expect`;
      angle.bodyFocus = cleanedProblem
        ? `build curiosity around how ${productName} solves ${cleanedProblem} and delivers ${primaryBenefit}${secondaryBenefit ? `, plus ${secondaryBenefit}` : ''}—create moments that make viewers want to share`
        : `build curiosity around how ${productName} delivers ${primaryBenefit}${secondaryBenefit ? ` and ${secondaryBenefit}` : ''}—create shareable moments`;
      angle.payoffFocus = `the surprising result: ${primaryBenefit}${cleanedProblem ? ` by solving ${cleanedProblem}` : ''}—something worth sharing`;
      // CTA focus based on campaign goal
      if (goalPhrase) {
        if (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions')) {
          angle.ctaFocus = `drive sales through sharing—make ${cleanedAudience || 'viewers'} want to tag someone who ${cleanedProblem ? `deals with ${cleanedProblem}` : `needs ${primaryBenefit}`} and encourage them to try ${productName}`;
        } else if (goal.toLowerCase().includes('engagement') || goal.toLowerCase().includes('community')) {
          angle.ctaFocus = `maximize engagement—make ${cleanedAudience || 'viewers'} want to tag someone who ${cleanedProblem ? `deals with ${cleanedProblem}` : `needs ${primaryBenefit}`}, comment their experience, or share this content`;
        } else if (goal.toLowerCase().includes('views') || goal.toLowerCase().includes('reach')) {
          angle.ctaFocus = `maximize reach—make ${cleanedAudience || 'viewers'} want to share this content, tag someone who ${cleanedProblem ? `deals with ${cleanedProblem}` : `needs ${primaryBenefit}`}, or save it for later`;
        } else {
          angle.ctaFocus = `encourage sharing and engagement—make ${cleanedAudience || 'viewers'} want to tag someone who ${cleanedProblem ? `deals with ${cleanedProblem}` : `needs ${primaryBenefit}`} or comment their experience`;
        }
      } else {
        angle.ctaFocus = `encourage sharing and engagement—make ${cleanedAudience || 'viewers'} want to tag someone who ${cleanedProblem ? `deals with ${cleanedProblem}` : `needs ${primaryBenefit}`} or comment their experience`;
      }
      angle.transformation = cleanedProblem
        ? `from ${cleanedProblem} to ${primaryBenefit}—unexpectedly`
        : `to ${primaryBenefit}—surprisingly`;
    }
    
    return {
      ...angle,
      productName,
      problem: cleanedProblem,
      primaryBenefit,
      secondaryBenefit,
      goalPhrase,
      audience: cleanedAudience || 'viewers'
    };
  };

  // Generate story flow instructions for body lines based on style, position, and script length
  const getStoryFlowForBodyLine = (lineIndex, totalBodyLines, style, briefData = {}, scriptLength = 30) => {
    // Determine the script angle/vision upfront
    const angle = determineScriptAngle(style, briefData);
    
    const productName = angle.productName;
    const problem = angle.problem;
    const primaryBenefit = angle.primaryBenefit;
    const secondaryBenefit = angle.secondaryBenefit;
    const audience = angle.audience;
    
    if (isTakeInspiration && selectedTrendingVideo?.stylePrompt) {
      // For trending video inspiration, create a story flow based on the style prompt
      const bodyMatch = selectedTrendingVideo.stylePrompt.match(/BODY CONTENT:([\s\S]*?)(?=CALL-TO-ACTION:|VISUAL STYLE:|TONE|PACING|$)/i);
      if (bodyMatch) {
        const bodyContent = bodyMatch[1].trim();
        const bodyBullets = bodyContent.split('\n')
          .map(line => line.trim())
          .filter(line => line && (line.startsWith('-') || line.startsWith('*') || line.startsWith('•')))
          .map(line => line.replace(/^[-*•]\s*/, '').trim())
          .filter(line => line.length > 10);
        
        if (bodyBullets.length > 0 && lineIndex < bodyBullets.length) {
          return bodyBullets[lineIndex];
        }
      }
    }
    
    // Style-specific story flows - Flowing script outline format
    if (style === 'trend' || style === 'trending') {
      // MOMENT (Body/Story/Demo) - Trend-based structure
      if (lineIndex === 0) {
        return `Right after the hook, show your discovery experience${problem ? `—demonstrate how ${productName} addresses ${problem}` : `—show how ${productName} delivers ${primaryBenefit}`}.\n\nThe vibe: fresh discovery, aligned with current trends, exciting first experience.\n\nWhat to accomplish: Show ${angle.bodyFocus} in a way that feels current and trending.\n\nStory direction: Talk about what happened when you tried ${productName}${problem ? ` for ${problem}` : ''}. Show ${productName} visually in ways that match what's performing well right now. Use trending audio, sounds, or formats. Keep the speed and pacing aligned with current trends.\n\nThe story you're building: ${angle.coreMessage}.`;
      } else if (lineIndex === totalBodyLines - 1) {
        // PAYOFF - The "aha" moment
        return `This is the "aha" moment—show ${angle.payoffFocus}.\n\nThe vibe: satisfying payoff, transformation complete, trending momentum maintained.\n\nWhat to accomplish: Reinforce ${primaryBenefit} as the strongest selling point${problem ? ` by showing how ${productName} solved ${problem}` : ''}.\n\nStory direction: Show the transformation: ${angle.transformation}. Summarize your experience with ${productName} in a punchy, satisfying way. Show the result or transformation visually. Keep it aligned with current trends and maintain the trending momentum.`;
      } else {
        const benefitToMention = lineIndex === 1 ? primaryBenefit : (secondaryBenefit || primaryBenefit);
        return `Continue building the story—${angle.bodyFocus}.\n\nThe vibe: momentum building, energy high, trending and current.\n\nWhat to accomplish: Share specific moments or results${lineIndex === 1 ? `—highlight ${primaryBenefit}` : lineIndex === 2 && secondaryBenefit ? `—showcase ${secondaryBenefit}` : ''}.\n\nStory direction: Show ${productName} in action—demonstrate how it${problem ? ` solves ${problem}` : ''} and delivers ${benefitToMention}. Use trending audio, sounds, or formats. Keep the energy and pacing aligned with current trends. Show ${productName} in ways that match what's performing well right now.`;
      }
    } else if (style === 'competitive') {
      // MOMENT (Body/Story/Demo) - Competitive: Real-world problems, authentic demonstrations
      if (lineIndex === 0) {
        return `Right after the hook, demonstrate ${productName}${problem ? ` solving ${problem}` : ''} in real situations.\n\nThe vibe: authentic, genuine, like showing a friend how it actually works.\n\nWhat to accomplish: Show ${angle.bodyFocus} in everyday scenarios that ${audience} can relate to.\n\nStory direction: Show authentic use cases and how ${productName} works in real scenarios${problem ? `—talk about ${problem} and how ${productName} addresses it` : `—talk about how ${productName} delivers ${primaryBenefit}`}. Use genuine, conversational delivery that builds trust. Keep the pacing natural and relatable.\n\nThe story you're building: ${angle.coreMessage}.`;
      } else if (lineIndex === totalBodyLines - 1) {
        // PAYOFF - The "aha" moment
        return `This is the proof moment—show ${angle.payoffFocus}.\n\nThe vibe: confident but honest, real results, trustworthy.\n\nWhat to accomplish: Reinforce ${primaryBenefit} as the strongest selling point${problem ? ` by showing how ${productName} solved ${problem}` : ''}.\n\nStory direction: Show the transformation: ${angle.transformation}. Summarize why ${productName} works and what makes it different. Show real results and outcomes visually. Keep it punchy and satisfying. Use confident but honest language.`;
      } else {
        const benefitToMention = lineIndex === 1 ? primaryBenefit : (secondaryBenefit || primaryBenefit);
        return `Continue with authentic demonstrations—${angle.bodyFocus}.\n\nThe vibe: genuine, relatable, building trust through real demonstrations.\n\nWhat to accomplish: Show ${productName} in real-world scenarios${lineIndex === 1 ? `—highlight ${primaryBenefit}` : lineIndex === 2 && secondaryBenefit ? `—showcase ${secondaryBenefit}` : ''}.\n\nStory direction: Show product demos, comparisons, or proofs. Demonstrate how it${problem ? ` solves ${problem}` : ''} and delivers ${benefitToMention}. Talk about specific benefits and outcomes. Keep it genuine and relatable. Use authentic language that ${audience} can trust.`;
      }
    } else if (style === 'viral') {
      // MOMENT (Body/Story/Demo) - Viral: Curiosity, unexpected reveals, shareable moments
      if (lineIndex === 0) {
        return `Build viral momentum right after the hook—${angle.bodyFocus}.\n\nThe vibe: unexpected, shareable, makes people want to keep watching.\n\nWhat to accomplish: Create unexpected moments or reveals that make ${audience} want to keep watching${problem ? `—connect with ${problem} in a relatable way` : ''}.\n\nStory direction: Show quick cuts, dynamic visuals, unexpected angles. Talk about the reveal or moment${problem ? `—how ${productName} solves ${problem}` : `—how ${productName} delivers ${primaryBenefit}`}. Use quick pacing and moments that create shareable content. Keep the energy high and the visuals dynamic.\n\nThe story you're building: ${angle.coreMessage}.`;
      } else if (lineIndex === totalBodyLines - 1) {
        // PAYOFF - The "aha" moment
        return `This is the surprising result—show ${angle.payoffFocus}. The vibe: shareable payoff, surprising transformation, makes people want to share. What to accomplish: Reinforce ${primaryBenefit} as the strongest selling point in a shareable way${problem ? ` by showing how ${productName} solved ${problem}` : ''}. Story direction: Show the transformation: ${angle.transformation}. Show ${productName} in a way that creates engagement and conversation. Keep it punchy and satisfying. Make it shareable—create moments that make ${audience} want to share or comment. Keep the energy high and the pacing fast.`;
      } else {
        const benefitToMention = lineIndex === 1 ? primaryBenefit : (secondaryBenefit || primaryBenefit);
        return `Continue building viral momentum—${angle.bodyFocus}. The vibe: curiosity building, unexpected reveals, shareable moments. What to accomplish: Create unexpected moments or reveals${lineIndex === 1 ? `—highlight ${primaryBenefit}` : lineIndex === 2 && secondaryBenefit ? `—showcase ${secondaryBenefit}` : ''}. Story direction: Show quick cuts, dynamic visuals, unexpected angles, product in action. Talk about the moment or reveal${problem ? `—how ${productName} solves ${problem}` : `—how ${productName} delivers ${benefitToMention}`}. Use dynamic pacing and moments that stop the scroll. Show ${productName} in ways that create curiosity and engagement. Make it shareable.`;
      }
    }
    
    // Default flow
    const defaultFlows = [
      `Introduce ${productName} and show how it fits into your routine or solves your problem.`,
      `Demonstrate ${productName} in action. Show key features and how they work for you.`,
      `Highlight the benefits you're experiencing. Show why ${productName} makes a difference.`,
      `Reinforce the value and show the result. Make it clear why ${productName} is worth it.`
    ];
    return defaultFlows[Math.min(lineIndex, defaultFlows.length - 1)] || `Continue the story—show how ${productName} helps you.`;
  };

  // Generate action - example actions they can do that fit the script guideline
  const generateActionGuidelines = (lineType, lineIndex, totalBodyLines, style, briefData = {}, scriptLength = 30, isPrimary = true, campaignGoal = '') => {
    // Determine the script angle/vision to get context
    const angle = determineScriptAngle(style, briefData, campaignGoal);
    
    const productName = angle.productName;
    const problem = angle.problem;
    const primaryBenefit = angle.primaryBenefit;
    const audience = angle.audience;
    
    if (lineType === 'hook') {
      if (style === 'trend' || style === 'trending') {
        // Storyboard ideas for trending hook
        const visualIdeas = [];
        if (productName !== '[product]') {
          visualIdeas.push(`Open with ${productName} in your hands—maybe you're unboxing it, holding it up to the camera, or showing it for the first time`);
        }
        if (problem) {
          visualIdeas.push(`Start with a quick "before" moment showing ${problem}—maybe a split second of struggle or frustration`);
        }
        visualIdeas.push(`Use a trending camera angle—maybe a low angle looking up, a close-up of your face reacting, or a quick zoom-in`);
        visualIdeas.push(`Match trending audio with your visuals—sync your movements or cuts to the beat`);
        if (audience !== 'viewers') {
          visualIdeas.push(`Show yourself clearly as ${audience}—maybe in your ${audience} setting, wearing ${audience} gear, or in a context that ${audience} recognize`);
        }
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      } else if (style === 'competitive') {
        // Storyboard ideas for competitive hook
        const visualIdeas = [];
        if (audience !== 'viewers') {
          visualIdeas.push(`Set the scene as ${audience}—show yourself in a natural ${audience} environment or context`);
        }
        if (problem) {
          visualIdeas.push(`Show a relatable moment where ${problem} is happening—maybe you're in the middle of dealing with ${problem}, or showing the situation clearly`);
        }
        if (productName !== '[product]') {
          visualIdeas.push(`Introduce ${productName} naturally—maybe it's sitting on a table, you're holding it casually, or it's part of your ${audience !== 'viewers' ? audience : 'everyday'} setup`);
        }
        visualIdeas.push(`Use a steady, medium shot that feels like you're talking to a friend—not too close, not too far, just natural`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      } else if (style === 'viral') {
        // Storyboard ideas for viral hook
        const visualIdeas = [];
        visualIdeas.push(`Start with an unexpected angle—maybe filming from above looking down, from below looking up, or from the side at an unusual angle`);
        if (productName !== '[product]') {
          visualIdeas.push(`Show ${productName} in a surprising way—maybe it's doing something unexpected, or you're using it in a way viewers wouldn't expect`);
        }
        if (problem) {
          visualIdeas.push(`Create a "wait, what?" moment—maybe show a quick "before" with ${problem} that makes viewers curious about what happens next`);
        }
        visualIdeas.push(`Use a dramatic reveal or transition—maybe a quick cut, a zoom, or a camera movement that creates intrigue`);
        if (audience !== 'viewers') {
          visualIdeas.push(`Make it clear this is for ${audience}—maybe show yourself as ${audience} in an unexpected context or situation`);
        }
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      }
      return `Example actions:\n• Set up the scene where ${productName} becomes relevant${problem ? `—show ${problem} clearly in a way that ${audience} can relate to` : ''}\n• Show the context so viewers understand why this matters`;
    } else if (lineType === 'line') {
      if (lineIndex === 0) {
        // First body line - storyboard ideas
        if (style === 'trend' || style === 'trending') {
          const visualIdeas = [];
          visualIdeas.push(`Cut to ${productName} in action—maybe a close-up of it being used, or you interacting with it${audience !== 'viewers' ? ` as ${audience}` : ''}`);
          if (primaryBenefit !== 'the key benefit') {
            visualIdeas.push(`Show ${primaryBenefit} visually—maybe demonstrate it happening, or show the result of ${primaryBenefit}`);
          }
          if (problem) {
            visualIdeas.push(`Show how ${productName} addresses ${problem}—maybe a quick comparison, or the problem being solved in real-time`);
          }
          visualIdeas.push(`Use quick transitions—maybe jump cuts, smooth camera movements, or dynamic framing that matches trending styles`);
          visualIdeas.push(`Keep the pace fast—match the energy of trending videos with frequent visual changes or camera movements`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'competitive') {
          const visualIdeas = [];
          visualIdeas.push(`Show ${productName} being used in a real, everyday setting${audience !== 'viewers' ? ` as ${audience}` : ''}—maybe in your home, workspace, or natural environment`);
          if (problem) {
            visualIdeas.push(`Demonstrate how ${productName} addresses ${problem}—show it happening naturally, not staged`);
          }
          if (primaryBenefit !== 'the key benefit') {
            visualIdeas.push(`Show ${primaryBenefit} happening—maybe the result, the benefit in action, or the outcome`);
          }
          visualIdeas.push(`Use a steady shot that shows both you and ${productName} clearly—let viewers see it working in real-time`);
          visualIdeas.push(`Show genuine reactions—your real response to using ${productName}, not over-acted`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'viral') {
          const visualIdeas = [];
          visualIdeas.push(`Create an unexpected visual moment—maybe show ${productName} from a surprising angle, in an unusual context, or doing something viewers wouldn't expect`);
          if (problem) {
            visualIdeas.push(`Show how ${productName} solves ${problem} in a surprising way—maybe a quick reveal or unexpected outcome`);
          }
          visualIdeas.push(`Use a dramatic transition or reveal that builds curiosity—maybe a quick cut, zoom, or camera movement that makes viewers wonder what's next`);
          if (audience !== 'viewers') {
            visualIdeas.push(`Make it clear this is for ${audience}—show yourself as ${audience} in a way that creates intrigue`);
          }
          visualIdeas.push(`Keep the visual interesting and shareable—create moments that make people want to comment or tag someone`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        }
        return `Example actions:\n• Show ${productName} in action as you begin using it${audience !== 'viewers' ? ` as ${audience}` : ''}\n• Show it naturally fitting into your routine or day`;
      } else if (lineIndex === totalBodyLines - 1) {
        // Last body line (payoff) - storyboard ideas
        if (style === 'trend' || style === 'trending') {
          const visualIdeas = [];
          visualIdeas.push(`Show the result or transformation—maybe a before/after comparison, a split screen showing the change, or the final outcome`);
          if (problem) {
            visualIdeas.push(`Demonstrate how ${productName} solved ${problem}—show the problem gone or the solution working`);
          }
          if (primaryBenefit !== 'the key benefit') {
            visualIdeas.push(`Show ${primaryBenefit} visually—the benefit in action or the result of ${primaryBenefit}`);
          }
          visualIdeas.push(`Use trending visual styles—maybe split screens, quick cuts showing the result, or dynamic camera movements that match current trends`);
          visualIdeas.push(`Show your reaction—maybe excitement, surprise, or satisfaction that matches trending reaction styles`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'competitive') {
          const visualIdeas = [];
          visualIdeas.push(`Show the authentic result or outcome—demonstrate what actually happened${audience !== 'viewers' ? ` as ${audience}` : ''}`);
          if (problem) {
            visualIdeas.push(`Show how ${productName} solved ${problem}—the real result, not exaggerated`);
          }
          if (primaryBenefit !== 'the key benefit') {
            visualIdeas.push(`Show ${primaryBenefit}—the actual benefit or outcome you experienced`);
          }
          visualIdeas.push(`Use a steady, honest shot that shows real results—let viewers see the actual outcome`);
          visualIdeas.push(`Keep it genuine—show real results without overstating or making false claims`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'viral') {
          const visualIdeas = [];
          visualIdeas.push(`Create a dramatic reveal of the result—maybe a surprising transformation, unexpected outcome, or shocking moment`);
          if (problem) {
            visualIdeas.push(`Show how ${productName} solved ${problem} in a surprising way—the reveal that makes viewers want to share`);
          }
          if (primaryBenefit !== 'the key benefit') {
            visualIdeas.push(`Show ${primaryBenefit} in a shareable way—the benefit that makes people want to comment or tag someone`);
          }
          visualIdeas.push(`Show your reaction—maybe shock, excitement, or disbelief that makes ${audience} want to share or comment`);
          visualIdeas.push(`Use visual elements that create engagement—maybe gestures, expressions, or moments that invite interaction`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        }
        return `Example actions:\n• Show the result or outcome with ${productName}${problem ? `—demonstrate how it solved ${problem}` : ''}\n• Show what changed or what happened`;
      } else {
        // Middle body lines - storyboard ideas
        const benefitToShow = lineIndex === 1 ? primaryBenefit : (angle.secondaryBenefit || primaryBenefit);
        if (style === 'trend' || style === 'trending') {
          const visualIdeas = [];
          visualIdeas.push(`Show ${productName} from different angles or in different moments—maybe multiple shots or perspectives`);
          if (benefitToShow !== 'the key benefit') {
            visualIdeas.push(`Highlight ${benefitToShow}—show it happening or demonstrate it visually`);
          }
          visualIdeas.push(`Use quick cuts or transitions that match trending styles—keep the energy high`);
          visualIdeas.push(`Show yourself interacting with ${productName}${audience !== 'viewers' ? ` as ${audience}` : ''} in ways that feel fresh and current`);
          visualIdeas.push(`Keep the visuals engaging—match the pace and style of trending videos`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'competitive') {
          const visualIdeas = [];
          visualIdeas.push(`Show ${productName} in practical scenarios${audience !== 'viewers' ? ` as ${audience}` : ''}—maybe using it in different situations or showing different features`);
          if (benefitToShow !== 'the key benefit') {
            visualIdeas.push(`Demonstrate ${benefitToShow}—show it working or the benefit in action`);
          }
          visualIdeas.push(`Use steady, honest framing that shows real use cases—keep it genuine`);
          visualIdeas.push(`Show how it works in everyday life—real situations that ${audience} can relate to`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        } else if (style === 'viral') {
          const visualIdeas = [];
          visualIdeas.push(`Show ${productName} in unexpected or surprising ways${benefitToShow !== 'the key benefit' ? `, revealing ${benefitToShow}` : ''}—maybe in unusual contexts or from creative angles`);
          visualIdeas.push(`Create moments that build curiosity—maybe surprising results, unexpected uses, or creative perspectives${audience !== 'viewers' ? ` that ${audience} can relate to` : ''}`);
          visualIdeas.push(`Keep the visuals interesting and shareable—build toward the big reveal or payoff moment`);
          return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
        }
        return `Example actions:\n• Show ${productName} in action, demonstrating key features${benefitToShow !== 'the key benefit' ? ` like ${benefitToShow}` : ''}\n• Show it working and what it does`;
      }
    } else if (lineType === 'cta') {
      if (style === 'trend' || style === 'trending') {
        const visualIdeas = [];
        visualIdeas.push(`Look directly at the camera—make eye contact with ${audience !== 'viewers' ? audience : 'viewers'}`);
        if (campaignGoal && campaignGoal.toLowerCase().includes('sales')) {
          visualIdeas.push(`Hold ${productName} or point to where to get it—maybe gesture toward a link or show the product clearly`);
        } else if (campaignGoal && campaignGoal.toLowerCase().includes('engagement')) {
          visualIdeas.push(`Gesture to comment or share—maybe point down, make a comment gesture, or invite interaction`);
        } else {
          visualIdeas.push(`Gesture to save, follow, or share—maybe point to save, make a follow gesture, or encourage sharing`);
        }
        visualIdeas.push(`Use popular hand gestures or poses that are trending—match current CTA styles`);
        visualIdeas.push(`Use quick cuts or dynamic camera movement—keep the energy high and engaging`);
        return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      } else if (style === 'competitive') {
        const visualIdeas = [];
        visualIdeas.push(`Look directly at the camera with genuine confidence—make eye contact with ${audience !== 'viewers' ? audience : 'viewers'}`);
        if (productName !== '[product]') {
          visualIdeas.push(`Have ${productName} visible in the frame—maybe holding it, showing it, or having it nearby`);
        }
        if (primaryBenefit !== 'the key benefit') {
          visualIdeas.push(`Show the result of ${primaryBenefit}—maybe demonstrate it, show the outcome, or reference the benefit visually`);
        }
        visualIdeas.push(`Use a steady, honest shot that builds trust—keep it genuine and confident`);
        visualIdeas.push(`Show yourself in a way that feels confident but not over-the-top—like you're genuinely recommending something that worked`);
        return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      } else if (style === 'viral') {
        const visualIdeas = [];
        visualIdeas.push(`Create a visual that encourages sharing—maybe show yourself reacting, gesturing, or creating a shareable moment`);
        if (campaignGoal && campaignGoal.toLowerCase().includes('engagement')) {
          visualIdeas.push(`Ask a question visually—maybe gesture to comment, invite ${audience} to share their experience, or create interaction`);
        } else {
          visualIdeas.push(`Make a gesture that invites comments or makes people want to tag someone${audience !== 'viewers' ? ` who is ${audience}` : ''}—maybe point, gesture, or create engagement`);
        }
        if (productName !== '[product]') {
          visualIdeas.push(`Show ${productName} or the result—maybe hold it, show it, or reference it visually`);
        }
        visualIdeas.push(`Use visual elements that create engagement—maybe expressions, gestures, or moments that invite participation`);
        return `Example actions:\n${visualIdeas.map(idea => `• ${idea}`).join('\n')}`;
      }
      return `Example actions:\n• Look directly at the camera${productName !== '[product]' ? ` with ${productName}` : ''}\n• Encourage ${audience} to take action\n• Show clear, engaging visuals that invite response`;
    }
    
    return '';
  };

  // UGC Advisor: Analyzes each script line and provides specific personalization guidance
  // Acts as a UGC advisor to help creators add value to each line
  const generatePersonalizationTip = (scriptLine, lineType, problem, primaryBenefit, productName, audience, lineIndex = 0, totalBodyLines = 1, previousLineContext = '', style = 'trend') => {
    // Hooks and CTAs should not be personalized - return empty string
    if (lineType === 'hook' || lineType === 'cta') {
      return '';
    }
    
    // Only body lines can be personalized
    if (lineType !== 'line') {
      return '';
    }
    
    if (!scriptLine || typeof scriptLine !== 'string' || scriptLine.trim().length === 0) {
      return '';
    }
    
    const lowerScript = scriptLine.toLowerCase();
    const isFirstBody = lineIndex === 0;
    const isLastBody = lineIndex === totalBodyLines - 1;
    const isMiddleBody = !isFirstBody && !isLastBody;
    
    // UGC Advisor Prompt: Analyze this specific line
    const analysis = {
      lineContent: scriptLine,
      mentionsProblem: problem && lowerScript.includes(problem.toLowerCase()),
      mentionsProduct: productName && lowerScript.includes(productName.toLowerCase()),
      mentionsBenefit: primaryBenefit && lowerScript.includes(primaryBenefit.toLowerCase()),
      isQuestion: scriptLine.trim().endsWith('?'),
      isPersonal: lowerScript.includes('i ') || lowerScript.includes('my ') || lowerScript.includes('me'),
      isDirective: lowerScript.includes('you ') || lowerScript.includes('your ') || lowerScript.includes('try ') || lowerScript.includes('get '),
      mentionsAction: lowerScript.includes('tried') || lowerScript.includes('used') || lowerScript.includes('discovered') || lowerScript.includes('found'),
      mentionsResult: lowerScript.includes('result') || lowerScript.includes('changed') || lowerScript.includes('helped') || lowerScript.includes('got') || lowerScript.includes('noticed'),
      mentionsEmotion: lowerScript.includes('love') || lowerScript.includes('amazing') || lowerScript.includes('game-changer') || lowerScript.includes('surprised'),
      wordCount: scriptLine.split(' ').length,
      position: isFirstBody ? 'first' : isLastBody ? 'last' : 'middle'
    };
    
    // Generate specific guidance based on this line's unique characteristics
    let guidance = '';
    let example = '';
    
    // Position-based guidance
    if (isFirstBody) {
      guidance += `This is your opening body line—it sets up the story. `;
      if (analysis.mentionsAction && !analysis.mentionsProblem) {
        guidance += `Add context BEFORE the action: Where were you when you first tried ${productName}? What made you decide to try it? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I was at the gym when my feet were soaked, and I saw someone wearing ${productName}. I decided to try them because I was tired of changing socks mid-workout."`;
      }
      if (analysis.mentionsProblem && !analysis.mentionsAction) {
        guidance += `Make the problem real: Share a specific moment when ${problem} affected you. Where were you? What were you doing? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I remember during my morning run last week when ${problem} got so bad I had to stop. I was halfway through my 5K and it was really affecting my pace."`;
      }
    } else if (isLastBody) {
      guidance += `This is your payoff line—show the transformation. `;
      if (analysis.mentionsResult) {
        guidance += `Be specific about the transformation: What exactly changed? What can you do now that you couldn't before? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "After using ${productName} for two weeks, I noticed my feet stay completely dry. Now I can do back-to-back classes without changing, which I could never do before."`;
      } else {
        guidance += `Add the transformation: After using ${productName}, what specifically changed? How did it feel? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "After using ${productName}, my workouts feel completely different. I'm not distracted by discomfort anymore."`;
      }
    } else {
      guidance += `This is a middle line—build depth. `;
      if (analysis.mentionsProduct && !analysis.mentionsAction && !analysis.mentionsResult) {
        guidance += `Add a specific moment: When do you use ${productName}? What happens when you use it? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I use ${productName} every morning for my runs. Last week during a rainy 10K, they kept my feet dry the entire time."`;
      }
    }
    
    // Content-based specific guidance with examples
    if (analysis.mentionsProblem && analysis.mentionsAction) {
      // Line mentions both problem and action
      guidance += `Instead of "I tried ${productName} for ${problem}", tell the full story: "I was [specific location/activity] dealing with ${problem} when [what happened that made you try it]. I decided to try ${productName} because [your specific reason]. At the time, I was [what was happening in your life]". `;
      example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I was at the gym when ${problem} got so bad I had to stop. I saw ${productName} recommended and tried them because I was training for a marathon."`;
    } else if (analysis.mentionsProblem && !analysis.mentionsAction) {
      // Line mentions problem but not action
      guidance += `Make ${problem} real and specific. Share: "I remember when ${problem} happened during [specific situation]—I was [doing what] and it really affected [what aspect]. I felt [how you felt] because [why it mattered]". `;
      example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I remember when ${problem} happened during my run last week—I was at mile 3 and it really affected my pace."`;
    } else if (analysis.mentionsAction && !analysis.mentionsProblem) {
      // Line mentions action but not problem
      if (analysis.mentionsProduct) {
        guidance += `Share your discovery moment: "I first heard about ${productName} when [how you found it]—I was [doing what] and [what made you curious]. I decided to try it because [your reason]". `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I first heard about ${productName} when my trainer recommended them. I decided to try them because I trust her recommendations."`;
      } else {
        guidance += `Add context to the action: When exactly did this happen? Where were you? What made you do this? `;
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "This happened last Thursday during my evening run. I was on my usual route when I realized how much more comfortable I felt."`;
      }
    } else if (analysis.mentionsResult) {
      // Line mentions results/transformation
      guidance += `Be specific about the transformation: "After using ${productName} for [time period], I noticed [specific change]. Now I can [what you can do now] that I couldn't before". `;
      example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "After using ${productName} for two weeks, I noticed my feet stay completely dry. Now I can do double sessions without changing, which I could never do before."`;
    } else if (analysis.mentionsEmotion) {
      // Line mentions emotion/feeling
      guidance += `Explain WHY you feel this way: "What I love about ${productName} is [specific feature/benefit] because [how it helps you personally]. For example, [specific situation where it helped]". `;
      example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "What I love about ${productName} is the moisture-wicking technology because it keeps my feet dry during long runs. During my 15K last weekend, my feet felt fresh the entire time."`;
    } else if (analysis.mentionsProduct && !analysis.mentionsAction && !analysis.mentionsResult) {
      // Line just mentions product
      guidance += `Connect ${productName} to your life: "I use ${productName} when [specific situation] because [your reason]". `;
      example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I use ${productName} every morning for my runs because they keep my feet dry. I can go straight from my run to my day without worrying."`;
    }
    
    // Add value suggestions based on what's missing
    if (!analysis.isPersonal && analysis.isDirective) {
      guidance += `Make it about YOU, not "you". Replace "you" statements with your personal experience. `;
      if (!example) {
        const personalized = scriptLine.replace(/you/gi, 'I').replace(/your/gi, 'my');
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "${personalized}" (make it about your experience, not the viewer's)`;
      }
    }
    
    if (analysis.wordCount < 15 && !analysis.mentionsAction && !analysis.mentionsResult) {
      guidance += `This line is short—expand it with one specific detail: when, where, or what happened. `;
      if (!example) {
        // Create contextually relevant example based on what the line mentions
        let expansion = '';
        if (analysis.mentionsProblem) {
          expansion = `Last week during my workout, ${problem} got so bad I had to stop.`;
        } else if (analysis.mentionsProduct) {
          expansion = `I first tried ${productName} last month and it changed everything.`;
        } else {
          expansion = `I noticed this during my morning routine last Tuesday.`;
        }
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "${scriptLine} ${expansion}"`;
      }
    }
    
    if (!analysis.mentionsProblem && !analysis.mentionsResult && isMiddleBody) {
      guidance += `Connect this to the story—add one specific detail about how this relates to ${problem || primaryBenefit}. `;
      if (!example) {
        // Create contextually relevant connection
        let connection = '';
        if (analysis.mentionsProduct) {
          connection = `This really helps with ${problem || primaryBenefit}—I noticed it during my last workout.`;
        } else {
          connection = `This connects to ${problem || primaryBenefit} because I experienced it firsthand.`;
        }
        example = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "${scriptLine} ${connection}"`;
      }
    }
    
    // Format the guidance with example
    if (guidance.trim().length > 0) {
      let result = `${guidance.trim()}`;
      if (example) {
        result += `\n\n${example}`;
      }
      return result;
    }
    
    // Fallback guidance with example
    const fallbackExample = `Example:\nCurrent: "${scriptLine}"\nPersonalized: "I remember when I first used ${productName} during my morning run. My feet stayed completely dry the entire time, and I didn't have to worry about changing socks."`;
    return `Add your personal story to this line. Include one specific detail: when this happened, where you were, or what you were doing. Make it YOUR story, not a template.\n\n${fallbackExample}`;
  };

  // Simple script line generator - creates a basic script line when Python doesn't provide one
  const generateSimpleScriptLine = (lineType, style, briefData, problem, primaryBenefit, productName, audience) => {
    if (lineType === 'hook') {
      if (style === 'trend' || style === 'trending') {
        if (problem) {
          return `Ever had ${problem}? Let me show you how ${productName} changed that for me.`;
        } else {
          return `POV: you just discovered ${productName} that actually delivers ${primaryBenefit}.`;
        }
      } else if (style === 'competitive') {
        if (problem) {
          return `I used to struggle with ${problem} until I found ${productName}.`;
        } else {
          return `For ${audience} who need ${primaryBenefit}, ${productName} has been a game-changer.`;
        }
      } else if (style === 'viral') {
        if (problem) {
          return `Wait, ${productName} actually solves ${problem}?`;
        } else {
          return `I can't believe ${productName} actually delivers ${primaryBenefit}.`;
        }
      }
    } else if (lineType === 'cta') {
      const goal = briefData.goal || '';
      if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
        return `Try ${productName} if you're dealing with ${problem || 'this'}—link in bio.`;
      } else if (goal && (goal.toLowerCase().includes('engagement'))) {
        return `Comment if ${productName} helped with ${problem || 'this'}.`;
      } else {
        return `Save this if you want ${primaryBenefit}.`;
      }
    }
    // Default for body lines
    if (problem) {
      return `So I tried ${productName} for ${problem} and here's what happened.`;
    } else {
      return `Let me show you how ${productName} delivers ${primaryBenefit}.`;
    }
  };

  // NEW GUIDELINE SYSTEM - Generate script line (what to say) with specific guidance and examples
  // This generates the "text" field - what the creator needs to say with pointers and guidance
  const generateScriptLineGuidance = (lineType, lineIndex, totalOfType, totalBodyLines, style, briefData = {}, scriptLength = 30, isPrimary = true, previousLineContext = '', campaignGoal = '') => {
    const angle = determineScriptAngle(style, briefData, campaignGoal);
    const detailLevel = getScriptDetailLevel(scriptLength);
    const pacing = getPacingGuidance(scriptLength, style);
    
    const productName = angle.productName;
    const problem = angle.problem;
    const primaryBenefit = angle.primaryBenefit;
    const secondaryBenefit = angle.secondaryBenefit;
    const goalPhrase = angle.goalPhrase;
    const goal = campaignGoal || briefData.goal || '';
    const audience = angle.audience;
    
    if (lineType === 'hook') {
      if (isPrimary) {
        // HOOK - Start the video/storyline that hooks audience and fits AI script style
        if (style === 'trend' || style === 'trending') {
          let guidance = `Start your video with a trending discovery moment.\n\n`;
          guidance += `Your goal: Hook viewers in the first 2-3 seconds using current trends. Create immediate curiosity${problem ? ` about solving ${problem}` : ` about getting ${primaryBenefit}`}.\n\n`;
          guidance += `What to say (example phrases):\n`;
          if (problem) {
            guidance += `• "POV: you just found ${productName} that actually solves ${problem}"\n`;
            guidance += `• "Ever tried ${productName}? Here's what happened when I used it for ${problem}..."\n`;
            guidance += `• "${productName} changed everything—no more ${problem}"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "I was struggling with ${problem} until I discovered ${productName}"\n`;
            }
          } else {
            guidance += `• "POV: you just discovered ${productName} that delivers ${primaryBenefit}"\n`;
            guidance += `• "Ever tried ${productName}? Here's what happened..."\n`;
            guidance += `• "${productName} changed everything for me"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "I needed ${primaryBenefit} and found ${productName}"\n`;
            }
          }
          guidance += `\nUse trending formats, sounds, or camera movements. Reference what's working right now. ${pacing}. Keep the energy high.\n\n`;
          guidance += `The story you're starting: ${angle.coreMessage}`;
          return guidance;
        } else if (style === 'competitive') {
          let guidance = `Start by addressing a relatable moment your audience understands.\n\n`;
          guidance += `Your goal: Build trust from the first moment by connecting with ${audience}${problem ? ` who understand ${problem}` : ''}.\n\n`;
          guidance += `What to say (example phrases):\n`;
          if (problem) {
            guidance += `• "I used to struggle with ${problem} until I found ${productName}"\n`;
            guidance += `• "If you're dealing with ${problem}, this might help"\n`;
            guidance += `• "For ${audience} who need ${primaryBenefit}, ${productName} has been a game-changer"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "Anyone else dealing with ${problem}? ${productName} actually works"\n`;
            }
          } else {
            guidance += `• "For ${audience} who need ${primaryBenefit}, ${productName} has been a game-changer"\n`;
            guidance += `• "If you want ${goalPhrase || primaryBenefit}, ${productName} actually works"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "I've been using ${productName} for ${primaryBenefit} and it's been amazing"\n`;
            }
          }
          guidance += `\nBe genuine and conversational—no sales pitch. Introduce ${productName} naturally as something that worked for you. ${pacing}.\n\n`;
          guidance += `The story you're telling: ${angle.coreMessage}`;
          return guidance;
        } else if (style === 'viral') {
          let guidance = `Start with an unexpected reveal that makes people stop scrolling.\n\n`;
          guidance += `Your goal: Create immediate intrigue that makes ${audience} want to know more and share it.\n\n`;
          guidance += `What to say (example phrases):\n`;
          if (problem) {
            guidance += `• "Wait, ${productName} actually solves ${problem}?"\n`;
            guidance += `• "I can't believe ${productName} works for ${problem}"\n`;
            guidance += `• "POV: you find out ${productName} exists and it fixes ${problem}"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "This ${productName} is about to change everything for ${audience} dealing with ${problem}"\n`;
            }
          } else {
            guidance += `• "Wait, ${productName} does WHAT?"\n`;
            guidance += `• "I can't believe ${productName} actually delivers ${primaryBenefit}"\n`;
            guidance += `• "POV: you find out ${productName} exists"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "This ${productName} is about to change everything"\n`;
            }
          }
          guidance += `\nUse bold statements, unexpected angles, or surprising reveals. Make it shareable—create that moment that makes ${audience} want to tag someone or comment. ${pacing}.\n\n`;
          guidance += `The story you're building: ${angle.coreMessage}`;
          return guidance;
        }
      } else {
        // Additional hook - variation guideline
        return `Create an alternative hook using a different angle, format, or approach. The vibe and core message stay the same: ${angle.coreMessage}. Explore different styles or perspectives, but always accomplish: ${angle.hookFocus}.`;
      }
    } else if (lineType === 'line') {
      // BODY LINE - Lead the creator through the story arc
      const isFirstBody = lineIndex === 0;
      const isLastBody = lineIndex === totalBodyLines - 1;
      const storyPosition = isFirstBody ? 'first' : isLastBody ? 'final' : 'middle';
      const storyBeat = isFirstBody ? 'discovery/demo' : isLastBody ? 'transformation/payoff' : 'development/depth';
      
      if (style === 'trend' || style === 'trending') {
        // Define the story structure upfront
        const storyType = problem ? 'POV discovery story' : 'trending demo story';
        let guidance = '';
        
        if (isFirstBody) {
          guidance += `Story beat: This is the DISCOVERY moment. You're showing ${audience} how you found ${productName}${problem ? ` that solves ${problem}` : ` that delivers ${primaryBenefit}`}.\n\n`;
          guidance += `Your goal: Demonstrate the discovery experience${problem ? `—show how ${productName} addresses ${problem}` : `—show how ${productName} delivers ${primaryBenefit}`} in a trending format.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "So I tried ${productName}${problem ? ` for ${problem}` : ''} and here's what happened..."\n`;
          guidance += `• "When I first used ${productName}${problem ? ` to solve ${problem}` : ''}, I noticed..."\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "Let me show you how ${productName}${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`}..."\n`;
          }
          if (detailLevel === 'comprehensive') {
            guidance += `• "Here's my experience with ${productName}${problem ? ` for ${problem}` : ''}..."\n`;
          }
        } else if (isLastBody) {
          guidance += `Story beat: This is the TRANSFORMATION/PAYOFF moment. You're showing ${audience} the result—the "aha" moment where they see the value.\n\n`;
          guidance += `Your goal: Reveal the transformation—show the result that makes ${audience} want ${productName}.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "The result? ${primaryBenefit}${problem ? `—no more ${problem}` : ''}"\n`;
          guidance += `• "After using ${productName}, I got ${primaryBenefit}"\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "Here's the transformation: ${angle.transformation}"\n`;
          }
          if (detailLevel === 'comprehensive') {
            guidance += `• "This is why ${productName} works: ${primaryBenefit}${problem ? ` by solving ${problem}` : ''}"\n`;
          }
        } else {
          guidance += `Story beat: This is the DEVELOPMENT/DEPTH moment. You're adding layers to the story—${angle.bodyFocus}.\n\n`;
          guidance += `Your goal: Build depth into the story—show more about ${productName} and why it works.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "What I love about ${productName} is..."\n`;
          guidance += `• "Another thing about ${productName}..."\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "Let me show you how ${productName}${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`} in real-time"\n`;
          }
          if (detailLevel === 'comprehensive' && secondaryBenefit) {
            guidance += `• "Plus, ${productName} also gives you ${secondaryBenefit}"\n`;
          }
        }
        
        guidance += `\n${pacing}. Use trending audio, sounds, or formats. Keep the energy high. This is part of the ${storyType} story arc: ${angle.coreMessage}.\n\n`;
        guidance += `The complete story arc: ${angle.coreMessage}`;
        return guidance;
      } else if (style === 'competitive') {
        // Define the story structure upfront
        const storyType = problem ? 'authentic problem-solution story' : 'trust-building demo story';
        let guidance = '';
        
        if (isFirstBody) {
          guidance += `Story beat: This is the DEMONSTRATION moment. You're showing ${audience} how ${productName}${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`} in real situations.\n\n`;
          guidance += `Your goal: Demonstrate ${productName}${problem ? ` solving ${problem}` : ''} authentically—build trust through real use cases.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "So here's how ${productName}${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`}..."\n`;
          guidance += `• "Let me show you ${productName} in action${problem ? ` for ${problem}` : ''}..."\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "I've been using ${productName}${problem ? ` to deal with ${problem}` : ''} and here's what I noticed..."\n`;
          }
        } else if (isLastBody) {
          guidance += `Story beat: This is the PROOF/RESULT moment. You're showing ${audience} the real results that build trust.\n\n`;
          guidance += `Your goal: Show the proof—real results that make ${audience} trust ${productName}.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "The result? ${primaryBenefit}${problem ? `—${problem} is gone` : ''}"\n`;
          guidance += `• "After using ${productName}, I got ${primaryBenefit}"\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "This is why ${productName} works: ${angle.transformation}"\n`;
          }
        } else {
          guidance += `Story beat: This is the DEPTH/CREDIBILITY moment. You're adding layers to build trust—${angle.bodyFocus}.\n\n`;
          guidance += `Your goal: Build credibility—show more authentic demonstrations that prove ${productName} works.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "Here's another way ${productName}${problem ? ` helps with ${problem}` : ` delivers ${primaryBenefit}`}..."\n`;
          guidance += `• "What I noticed about ${productName} is..."\n`;
          if (detailLevel === 'comprehensive') {
            guidance += `• "Let me demonstrate ${productName} in a real scenario..."\n`;
          }
        }
        
        guidance += `\nBe genuine and relatable. Show real use cases. ${pacing}. This is part of the ${storyType} story arc: ${angle.coreMessage}.\n\n`;
        guidance += `The complete story arc: ${angle.coreMessage}`;
        return guidance;
      } else if (style === 'viral') {
        // Define the story structure upfront
        const storyType = problem ? 'viral reveal story' : 'shareable transformation story';
        let guidance = '';
        
        if (isFirstBody) {
          guidance += `Story beat: This is the CURIOSITY BUILDER moment. You're creating unexpected moments that make ${audience} want to keep watching.\n\n`;
          guidance += `Your goal: Build viral momentum—create intrigue that makes ${audience} need to see what happens next.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "Wait until you see what ${productName}${problem ? ` does for ${problem}` : ` delivers`}..."\n`;
          guidance += `• "Here's the thing about ${productName}..."\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "You won't believe how ${productName}${problem ? ` solves ${problem}` : ` works`}..."\n`;
          }
        } else if (isLastBody) {
          guidance += `Story beat: This is the SURPRISING RESULT moment. You're revealing the payoff—something worth sharing.\n\n`;
          guidance += `Your goal: Show the surprising result—create that shareable moment that makes ${audience} want to tag someone.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "The result? ${primaryBenefit}${problem ? `—${problem} is gone` : ''}"\n`;
          guidance += `• "I can't believe ${productName} actually${problem ? ` solved ${problem}` : ` delivered ${primaryBenefit}`}"\n`;
          if (detailLevel === 'detailed' || detailLevel === 'comprehensive') {
            guidance += `• "This is the transformation: ${angle.transformation}"\n`;
          }
        } else {
          guidance += `Story beat: This is the CURIOSITY DEPTH moment. You're adding layers of intrigue—${angle.bodyFocus}.\n\n`;
          guidance += `Your goal: Build more curiosity—keep ${audience} engaged with unexpected reveals.\n\n`;
          guidance += `What to say (example phrases):\n`;
          guidance += `• "But wait, there's more..."\n`;
          guidance += `• "Here's what's crazy about ${productName}..."\n`;
          if (detailLevel === 'comprehensive') {
            guidance += `• "You need to see how ${productName}${problem ? ` solves ${problem}` : ` works`}..."\n`;
          }
        }
        
        guidance += `\nCreate shareable moments. Use unexpected reveals. ${pacing}. This is part of the ${storyType} story arc: ${angle.coreMessage}.\n\n`;
        guidance += `The complete story arc: ${angle.coreMessage}`;
        return guidance;
      }
    } else if (lineType === 'cta') {
      if (isPrimary) {
        // CTA - End with campaign goal
        if (style === 'trend' || style === 'trending') {
          let guidance = `End your video by ${angle.ctaFocus}.\n\n`;
          guidance += `Your goal: Invite ${audience} to take the next step in a way that feels organic, not pushy.\n\n`;
          guidance += `What to say (example phrases):\n`;
          
          // Tailor CTA phrases based on campaign goal
          if (goal && (goal.toLowerCase().includes('sales') || goal.toLowerCase().includes('conversions'))) {
            if (problem) {
              guidance += `• "Try ${productName} if you're dealing with ${problem}—link in bio"\n`;
              guidance += `• "Get ${productName} now if ${problem} is something you struggle with"\n`;
              guidance += `• "Shop ${productName} to solve ${problem}—link in bio"\n`;
            } else {
              guidance += `• "Get ${productName} now—link in bio"\n`;
              guidance += `• "Shop ${productName} for ${primaryBenefit}—link in bio"\n`;
              guidance += `• "Try ${productName} today—link in bio"\n`;
            }
          } else if (goal && (goal.toLowerCase().includes('engagement') || goal.toLowerCase().includes('community'))) {
            if (problem) {
              guidance += `• "Comment if ${productName} helped with ${problem}"\n`;
              guidance += `• "Drop your experience with ${productName} below"\n`;
              guidance += `• "Tag someone who deals with ${problem}"\n`;
              guidance += `• "Share this if ${productName} worked for you"\n`;
            } else {
              guidance += `• "Comment if you've tried ${productName}"\n`;
              guidance += `• "Drop your thoughts below"\n`;
              guidance += `• "Tag someone who needs ${primaryBenefit}"\n`;
              guidance += `• "Share your experience with ${productName}"\n`;
            }
          } else if (goal && (goal.toLowerCase().includes('views') || goal.toLowerCase().includes('reach'))) {
            if (problem) {
              guidance += `• "Save this if you're dealing with ${problem}"\n`;
              guidance += `• "Follow for more on ${primaryBenefit}"\n`;
              guidance += `• "Share this with someone who needs ${problem} solved"\n`;
            } else {
              guidance += `• "Save this for later"\n`;
              guidance += `• "Follow for more on ${primaryBenefit}"\n`;
              guidance += `• "Share this with someone who needs ${primaryBenefit}"\n`;
            }
          } else {
            // Default phrases
            if (problem) {
              guidance += `• "Try ${productName} if you're dealing with ${problem}"\n`;
              guidance += `• "Save this for later if you want ${goalPhrase || `to solve ${problem}`}"\n`;
              guidance += `• "Drop a comment if ${productName} helped with ${problem}"\n`;
            } else {
              guidance += `• "Try ${productName} and let me know what you think"\n`;
              guidance += `• "Save this for later if you want ${goalPhrase || primaryBenefit}"\n`;
              guidance += `• "Drop a comment if you've tried ${productName}"\n`;
            }
          }
          
          guidance += `\nKeep it casual and engaging—use trending phrases. Add trending emojis or gestures. Make it feel like part of a conversation, not a sales pitch. ${pacing}.\n\n`;
          guidance += `Campaign goal: ${goal || 'general engagement'}`;
          return guidance;
        } else if (style === 'competitive') {
          let guidance = `End by ${angle.ctaFocus}.\n\n`;
          guidance += `Your goal: Reinforce why ${productName} works and who it's for, in a way that builds trust.\n\n`;
          guidance += `What to say (example phrases):\n`;
          if (problem) {
            guidance += `• "If you're dealing with ${problem}, ${productName} is worth trying—it delivers ${primaryBenefit}"\n`;
            guidance += `• "I'd recommend ${productName} to any ${audience} who needs ${primaryBenefit}"\n`;
            guidance += `• "This ${productName} actually works for ${problem}—give it a try"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "Trust me on this one—${productName} solves ${problem} and delivers ${primaryBenefit}"\n`;
            }
          } else {
            guidance += `• "I'd recommend ${productName} to any ${audience} who needs ${primaryBenefit}"\n`;
            guidance += `• "This ${productName} actually works—give it a try if you want ${goalPhrase || primaryBenefit}"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "Trust me on this one—${productName} delivers ${primaryBenefit}"\n`;
            }
          }
          guidance += `\nUse confident but honest language. Show authenticity, not hype. Be specific about who should try it and why—${audience} who ${problem ? `deal with ${problem}` : `need ${primaryBenefit}`}. ${pacing}.\n\n`;
          guidance += `Campaign goal: ${goal || 'general engagement'}`;
          return guidance;
        } else if (style === 'viral') {
          let guidance = `End by ${angle.ctaFocus}.\n\n`;
          guidance += `Your goal: Create a moment that makes ${audience} want to share, comment, or tag someone.\n\n`;
          guidance += `What to say (example phrases):\n`;
          if (problem) {
            guidance += `• "Tag someone who deals with ${problem}"\n`;
            guidance += `• "Comment if ${productName} helped with ${problem}"\n`;
            guidance += `• "Drop a 🔥 if ${problem} is something you deal with"\n`;
            guidance += `• "Share this with someone who struggles with ${problem}"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "What do you think? Has ${productName} helped with ${problem}? Drop your thoughts below"\n`;
            }
          } else {
            guidance += `• "Tag someone who needs ${primaryBenefit}"\n`;
            guidance += `• "Comment if you've tried ${productName}"\n`;
            guidance += `• "Drop a 🔥 if you want ${primaryBenefit}"\n`;
            guidance += `• "Share this with someone who needs ${primaryBenefit}"\n`;
            if (detailLevel !== 'concise') {
              guidance += `• "What do you think? Drop your thoughts below"\n`;
            }
          }
          guidance += `\nAsk questions, create debate, or invite participation. Make it feel like part of a viral conversation that ${audience} want to join. Encourage sharing and engagement. ${pacing}.\n\n`;
          guidance += `Campaign goal: ${goal || 'general engagement'}`;
          return guidance;
        }
      } else {
        // Additional CTA - variation guideline
        return `Create an alternative CTA using a different approach, tone, or urgency level. The vibe and core message stay the same: ${angle.coreMessage}. Explore different styles or formats, but always accomplish: ${angle.ctaFocus}.`;
      }
    }
    
    return '';
  };

  // Helper function to detect and format Python-generated example hooks/CTAs
  const formatPythonGeneratedExamples = (pythonText, lineType) => {
    if (!pythonText || typeof pythonText !== 'string') return null;
    
    const trimmed = pythonText.trim();
    
    // Check if it already contains example format (Example 1: ... | Example 2: ...)
    if (trimmed.includes('Example 1:') || trimmed.includes('Example 2:') || trimmed.includes('Example 3:')) {
      // Already in example format, return as is but enhance with guidance wrapper
      return trimmed;
    }
    
    // Check if it's a guideline (not a script line) - these should be ignored
    const guidelineIndicators = [
      'HOOK -', 'BODY LINE', 'CTA -', 'Your goal:', 'What to say', 'Guidance:', 
      'Story beat:', 'The story', 'The complete story', 'Campaign goal:',
      'Example phrases:', 'Example actions:', 'Suggestions:'
    ];
    const isGuideline = guidelineIndicators.some(indicator => trimmed.includes(indicator));
    if (isGuideline) {
      return null; // This is a guideline, not a script line
    }
    
    // Check if it's a quoted script line (actual script, not guideline)
    // Python generates: "Ever tried to take a pic, but your phone just says 'storage full'? Ugh!"
    const quotedMatch = trimmed.match(/^["'](.+?)["']$/);
    if (quotedMatch) {
      // It's a single script line, convert to example format
      const scriptLine = quotedMatch[1];
      return `Example 1: ${scriptLine}`;
    }
    
    // Check if it's an unquoted script line (actual spoken dialogue)
    // Script lines are usually:
    // - Short to medium length (under 200 chars for hooks/CTAs, under 300 for body)
    // - Start with capital letter or quote
    // - End with punctuation (or could be a question/exclamation)
    // - Don't contain guideline language
    // - Look like conversational speech
    const maxLength = lineType === 'line' ? 300 : 200;
    
    // More lenient check - if it looks like conversational speech, treat it as a script line
    const startsWithCapital = /^[A-Z"']/.test(trimmed);
    const endsWithPunctuation = /[.!?]$/.test(trimmed.trim());
    const isShortEnough = trimmed.length > 10 && trimmed.length < maxLength;
    const isSingleLine = !trimmed.includes('\n');
    const notGuidelineFormat = 
      !trimmed.includes('•') && // Not a bullet point
      !trimmed.toLowerCase().includes('example') && // Not already formatted
      !trimmed.toLowerCase().includes('guidance') && // Not guideline language
      !trimmed.toLowerCase().includes('your goal') && // Not guideline language
      !trimmed.toLowerCase().includes('what to say') && // Not guideline language
      !trimmed.toLowerCase().includes('story beat'); // Not guideline language
    
    const looksLikeScript = 
      isShortEnough &&
      startsWithCapital &&
      endsWithPunctuation &&
      isSingleLine &&
      notGuidelineFormat;
    
    if (looksLikeScript) {
      // Likely a script line, convert to example format
      return `Example 1: ${trimmed}`;
    }
    
    return null; // Not a script line, return null to use generated guidelines
  };

  const applyCreativeFlairInstructions = ({ parsedLines, contextTips, campaignGoal = '' }) => {
    // Count hooks, body lines, and CTAs separately
    const hooks = parsedLines.filter(l => l.type === 'hook');
    const bodyLines = parsedLines.filter(l => l.type === 'line');
    const ctas = parsedLines.filter(l => l.type === 'cta');
    const totalHooks = hooks.length;
    const totalBodyLines = bodyLines.length;
    const totalCTAs = ctas.length;
    const additionalHooks = Math.max(0, totalHooks - 1); // First hook is primary
    const additionalCTAs = Math.max(0, totalCTAs - 1); // First CTA is primary
    
    // Track indices for each type
    let hookIndex = 0;
    let bodyLineIndex = 0;
    let ctaIndex = 0;
    
    // Get script length for body line calculations
    const scriptLength = parseInt(scriptSettings.videoLength) || 30;
    
    // If "Take Inspiration" is selected, use the trending video's stylePrompt
    let baseGuidance = creativeFlairGuidanceMap;
    if (isTakeInspiration && selectedTrendingVideo?.stylePrompt) {
      const trendingFlow = parseTrendingVideoStylePrompt(selectedTrendingVideo.stylePrompt);
      if (trendingFlow) {
        baseGuidance = {
          hook: trendingFlow.hook || creativeFlairGuidanceMap.hook,
          line: trendingFlow.line || creativeFlairGuidanceMap.line,
          cta: trendingFlow.cta || creativeFlairGuidanceMap.cta
        };
      }
    } else {
      // Otherwise, get style-specific guidance based on aiScriptStyle
      const styleGuidance = creativeFlairGuidanceByStyle[aiScriptStyle] || creativeFlairGuidanceMap;
      baseGuidance = styleGuidance;
    }
    
    return parsedLines.map((line, index) => {
      // For hooks - preserve Python-generated example hooks if present, otherwise generate guidelines
      if (line.type === 'hook') {
        const isPrimaryHook = hookIndex === 0;
        let scriptGuideline = '';
        let pythonExamples = null; // Declare outside to use in debug log
        
        // Get angle data for personalization
        const angle = determineScriptAngle(aiScriptStyle, contextTips.briefData || {}, campaignGoal);
        const productName = angle.productName;
        const problem = angle.problem;
        const primaryBenefit = angle.primaryBenefit;
        const audience = angle.audience;
        
        // Check if Python generated example hooks
        pythonExamples = formatPythonGeneratedExamples(line.text, 'hook');
        
        // If "Take Inspiration" is selected, use the trending video's stylePrompt
        if (isTakeInspiration && selectedTrendingVideo?.stylePrompt && !pythonExamples) {
          const trendingFlow = parseTrendingVideoStylePrompt(selectedTrendingVideo.stylePrompt);
          if (trendingFlow && trendingFlow.hook) {
            scriptGuideline = trendingFlow.hook;
            if (!isPrimaryHook) {
              scriptGuideline = `Create an alternative hook variation. ${scriptGuideline} This is an additional hook, so explore different styles or perspectives.`;
            }
          }
        }
        
        // Store the actual script line before adding personalization tip
        let actualScriptLine = '';
        
        // If Python generated examples, use them directly with personalization advice (if needed)
        if (pythonExamples) {
          // Extract the actual script line from the example format
          actualScriptLine = pythonExamples.replace(/^Example \d+:\s*/, '').trim();
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'hook', problem, primaryBenefit, productName, audience);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        } else if (!scriptGuideline) {
          // If no Python examples, generate a simple script line
          actualScriptLine = generateSimpleScriptLine('hook', aiScriptStyle, contextTips.briefData || {}, problem, primaryBenefit, productName, audience);
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'hook', problem, primaryBenefit, productName, audience);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        } else {
          // If scriptGuideline was set from trending flow, extract the first line
          actualScriptLine = scriptGuideline.split('\n')[0] || scriptGuideline;
        }
        
        // Generate notes based on the actual script line
        const detailedNotes = generateDetailedNotes('hook', contextTips.briefData || {}, 0, 1, aiScriptStyle, scriptLength, campaignGoal, actualScriptLine);
        // Use Python action if available, otherwise generate
        const actionGuideline = line.action && line.action.trim() && !line.action.includes('Example actions:') 
          ? `Example actions:\n• ${line.action}` 
          : generateActionGuidelines('hook', hookIndex, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimaryHook, campaignGoal);
        
        hookIndex++;
        return {
          ...line,
          text: scriptGuideline || baseGuidance.hook,
          action: actionGuideline || line.action || '',
          notes: detailedNotes
        };
      }
      
      // For body lines, use Python-generated script lines if available, otherwise generate simple ones
      if (line.type === 'line') {
        // Get angle data for personalization
        const angle = determineScriptAngle(aiScriptStyle, contextTips.briefData || {}, campaignGoal);
        const productName = angle.productName;
        const problem = angle.problem;
        const primaryBenefit = angle.primaryBenefit;
        const audience = angle.audience;
        
        // Check if Python generated script line
        const pythonExamples = formatPythonGeneratedExamples(line.text, 'line');
        let scriptGuideline = '';
        let actualScriptLine = '';
        
        // Get previous line context for better personalization guidance
        const previousLine = parsedLines[index - 1];
        const previousLineContext = previousLine?.text ? previousLine.text.split('\n')[0] : '';
        
        if (pythonExamples) {
          // Extract the actual script line from the example format
          actualScriptLine = pythonExamples.replace(/^Example \d+:\s*/, '').trim();
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'line', problem, primaryBenefit, productName, audience, bodyLineIndex, totalBodyLines, previousLineContext, aiScriptStyle);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        } else {
          // Generate a simple script line
          const isFirstBody = bodyLineIndex === 0;
          const isLastBody = bodyLineIndex === totalBodyLines - 1;
          
          if (isFirstBody) {
            actualScriptLine = problem ? `So I tried ${productName} for ${problem} and here's what happened.` : `Let me show you how ${productName} delivers ${primaryBenefit}.`;
          } else if (isLastBody) {
            actualScriptLine = `The result? ${primaryBenefit}${problem ? `—no more ${problem}` : ''}.`;
          } else {
            actualScriptLine = `What I love about ${productName} is how it${problem ? ` solves ${problem}` : ` delivers ${primaryBenefit}`}.`;
          }
          
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'line', problem, primaryBenefit, productName, audience, bodyLineIndex, totalBodyLines, previousLineContext, aiScriptStyle);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        }
        
        // Generate notes based on the actual script line
        const detailedNotes = generateDetailedNotes('line', contextTips.briefData || {}, bodyLineIndex, totalBodyLines, aiScriptStyle, scriptLength, campaignGoal, actualScriptLine);
        const actionGuideline = line.action && line.action.trim() && !line.action.includes('Example actions:') 
          ? `Example actions:\n• ${line.action}` 
          : generateActionGuidelines('line', bodyLineIndex, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, true, campaignGoal);
        bodyLineIndex++;
        return {
          ...line,
          text: scriptGuideline,
          action: actionGuideline || line.action || '',
          notes: detailedNotes
        };
      }
      
      // For CTAs - preserve Python-generated example CTAs if present, otherwise generate guidelines
      if (line.type === 'cta') {
        const isPrimaryCTA = ctaIndex === 0; // First CTA is primary
        let scriptGuideline = '';
        let pythonExamples = null; // Declare outside to use in debug log
        
        // Check if Python generated example CTAs
        pythonExamples = formatPythonGeneratedExamples(line.text, 'cta');
        
        // If "Take Inspiration" is selected, use the trending video's stylePrompt
        if (isTakeInspiration && selectedTrendingVideo?.stylePrompt && !pythonExamples) {
          const trendingFlow = parseTrendingVideoStylePrompt(selectedTrendingVideo.stylePrompt);
          if (trendingFlow && trendingFlow.cta) {
            scriptGuideline = trendingFlow.cta;
            if (!isPrimaryCTA) {
              scriptGuideline = `Create an alternative CTA variation. ${scriptGuideline} This is an additional CTA, so explore different styles or formats.`;
            }
          }
        }
        
        // Get angle data for personalization
        const angle = determineScriptAngle(aiScriptStyle, contextTips.briefData || {}, campaignGoal);
        const productName = angle.productName;
        const problem = angle.problem;
        const primaryBenefit = angle.primaryBenefit;
        const audience = angle.audience;
        
        // Store the actual script line before adding personalization tip
        let actualScriptLine = '';
        
        // If Python generated examples, use them directly with personalization advice (if needed)
        if (pythonExamples) {
          // Extract the actual script line from the example format
          actualScriptLine = pythonExamples.replace(/^Example \d+:\s*/, '').trim();
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'cta', problem, primaryBenefit, productName, audience);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        } else if (!scriptGuideline) {
          // If no Python examples, generate a simple script line
          actualScriptLine = generateSimpleScriptLine('cta', aiScriptStyle, contextTips.briefData || {}, problem, primaryBenefit, productName, audience);
          const personalizationTip = generatePersonalizationTip(actualScriptLine, 'cta', problem, primaryBenefit, productName, audience);
          scriptGuideline = personalizationTip ? `${actualScriptLine}\n\n${personalizationTip}` : actualScriptLine;
        } else {
          // If scriptGuideline was set from trending flow, extract the first line
          actualScriptLine = scriptGuideline.split('\n')[0] || scriptGuideline;
        }
        
        // Generate notes based on the actual script line
        const detailedNotes = generateDetailedNotes('cta', contextTips.briefData || {}, 0, 1, aiScriptStyle, scriptLength, campaignGoal, actualScriptLine);
        // Use Python action if available, otherwise generate
        const actionGuideline = line.action && line.action.trim() && !line.action.includes('Example actions:') 
          ? `Example actions:\n• ${line.action}` 
          : generateActionGuidelines('cta', ctaIndex, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimaryCTA, campaignGoal);
        
        ctaIndex++;
        return {
          ...line,
          text: scriptGuideline || baseGuidance.cta,
          action: actionGuideline || line.action || '',
          notes: detailedNotes
        };
      }
      
      return line;
    });
  };

  // Fetch campaigns when component mounts
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return;

    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      const campaignsList = [];
      snapshot.forEach((doc) => {
        campaignsList.push({ id: doc.id, ...doc.data() });
      });
      setCampaigns(campaignsList);
      
      // Auto-select initialCampaign if provided and campaigns are loaded
      if (initialCampaign && !selectedCampaign && campaignsList.some(c => c.id === initialCampaign)) {
        setSelectedCampaign(initialCampaign);
      }
    }, (error) => {
      console.error('Error fetching campaigns:', error);
    });

    return () => unsubscribe();
  }, []);

  // Auto-select campaign when panel opens with initialCampaign
  useEffect(() => {
    if (open && initialCampaign && campaigns.length > 0) {
      // Check if the initialCampaign exists in the campaigns list
      const campaignExists = campaigns.some(c => c.id === initialCampaign);
      if (campaignExists && selectedCampaign !== initialCampaign) {
        setSelectedCampaign(initialCampaign);
      }
    }
  }, [open, initialCampaign, campaigns, selectedCampaign]);

  // Fetch briefs for selected campaign
  useEffect(() => {
    if (!selectedCampaign) {
      setBriefsList([]);
      return;
    }
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const briefsRef = collection(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs');
    const unsubscribe = onSnapshot(briefsRef, (snapshot) => {
      const fetchedBriefs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBriefsList(fetchedBriefs);
    });
    return () => unsubscribe();
  }, [selectedCampaign, open]);

  // Detect if "Take Inspiration" is selected (trend style + selectedTrendingVideo)
  const isTakeInspiration = aiScriptStyle === 'trend' && selectedTrendingVideo !== null;

  // Reset additional hooks/CTAs to 0 when "Take Inspiration" is selected
  useEffect(() => {
    if (isTakeInspiration) {
      if (scriptSettings.additionalHooks > 0 || scriptSettings.additionalCTAs > 0) {
        setScriptSettings(prev => ({
          ...prev,
          additionalHooks: 0,
          additionalCTAs: 0
        }));
      }
    }
  }, [isTakeInspiration, scriptSettings.additionalHooks, scriptSettings.additionalCTAs]);

  // Update script library when additional hooks/CTAs change
  useEffect(() => {
    const currentScriptLibraryLength = scriptLibrary.length;
    if (currentScriptLibraryLength > 0 && step === 5) {
      const currentScript = scriptLibrary[activeScriptIdx];
      if (currentScript) {
        const totalHooks = 1 + (scriptSettings.additionalHooks || 0);
        const totalCTAs = 1 + (scriptSettings.additionalCTAs || 0);
        
        // Count current hooks and CTAs
        const currentHooks = currentScript.lines.filter(line => line.type === 'hook').length;
        const currentCTAs = currentScript.lines.filter(line => line.type === 'cta').length;
        
        // Update lines if counts don't match
        if (currentHooks !== totalHooks || currentCTAs !== totalCTAs) {
          // Separate lines by type
          const hooks = [];
          const bodyLines = [];
          const ctas = [];
          
          for (const line of currentScript.lines) {
            if (line.type === 'hook') hooks.push(line);
            else if (line.type === 'cta') ctas.push(line);
            else bodyLines.push(line);
          }
          
          // Adjust hooks count
          if (hooks.length < totalHooks) {
            // Add new empty hooks
            while (hooks.length < totalHooks) {
              hooks.push({ type: 'hook', text: '', notes: '', action: '' });
            }
          } else if (hooks.length > totalHooks) {
            // Keep only first N hooks
            hooks.splice(totalHooks);
          }
          
          // Adjust CTAs count
          if (ctas.length < totalCTAs) {
            // Add new empty CTAs
            while (ctas.length < totalCTAs) {
              ctas.push({ type: 'cta', text: '', notes: '', action: '' });
            }
          } else if (ctas.length > totalCTAs) {
            // Keep only first N CTAs
            ctas.splice(totalCTAs);
          }
          
          // Reconstruct lines: hooks first, then body lines, then CTAs
          const newLines = [...hooks, ...bodyLines, ...ctas];
          
          // Update script library
          const updatedScripts = [...scriptLibrary];
          updatedScripts[activeScriptIdx] = {
            ...currentScript,
            lines: newLines
          };
          setScriptLibrary(updatedScripts);
        }
      }
    }
  }, [scriptSettings.additionalHooks, scriptSettings.additionalCTAs, mode, activeScriptIdx, step]);

  // Handle dynamic loading steps
  useEffect(() => {
    let interval;
    if (isLoading) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep(prev => {
          const nextStep = prev + 1;
          // Stop at the last step (Almost ready!)
          if (nextStep >= scriptLoadingSteps.length - 1) {
            clearInterval(interval);
            return scriptLoadingSteps.length - 1;
          }
          return nextStep;
        });
      }, 1200); // Change step every 1200ms
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  // Fetch trending videos from Firestore
  useEffect(() => {
    const fetchTrendingVideos = async () => {
      try {
        setLoadingTrendingVideos(true);
        const data = await getCurrentTrendingVideos();
        if (data && data.videos) {
          setTrendingVideos(data.videos);
        }
      } catch (error) {
        console.error('Error fetching trending videos:', error);
        setTrendingVideos([]);
      } finally {
        setLoadingTrendingVideos(false);
      }
    };

    fetchTrendingVideos();
  }, []);

  // Load selected brief data
  useEffect(() => {
    if (!selectedBriefId || !selectedCampaign) return;
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', selectedBriefId);
    getDoc(briefRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSelectedBrief(data);
      }
    }).catch(error => {
      console.error('Error loading brief:', error);
      setError('Failed to load brief data');
    });
  }, [selectedBriefId, selectedCampaign]);

  // Initialize component state when editing an existing script
  useEffect(() => {
    console.log('Script panel useEffect triggered - script:', script, 'open:', open);
    
    if (script && open) {
      console.log('Setting up for editing existing script');
      // Set the script name
      setNewScriptName(script.name || script.title || '');
      
      // Set script settings if they exist
      if (script.settings) {
        setScriptSettings(script.settings);
      }
      
      // Set script lines if they exist
      if (script.lines) {
        console.log('Loading script for editing:', script);
        setScriptLibrary([{
          id: script.id,
          name: script.name || script.title || 'Script 1',
          lines: script.lines,
          origin: script.origin || 'manual', // Preserve origin, default to 'manual' for legacy scripts
          settings: script.settings // Preserve settings if they exist
        }]);
        setActiveScriptIdx(0); // Set to first (and only) script
      }
      
      // Set campaign and brief if they exist
      if (script.campaignId || script.briefId) {
        setSelectedCampaign(script.campaignId || '');
        setSelectedBriefId(script.briefId || '');
      }
      
      // Start at step 5 (Script Editor) when editing
      console.log('Setting step to 5 for script editing');
      setStep(5);
      setMode('ai'); // Default to AI mode for editing
    } else if (!script && open) {
      console.log('Setting up for creating new script');
      // Reset state when creating a new script
      setNewScriptName('');
      setScriptSettings({
        platform: '',
        creativeControl: 'exact',
        videoLength: '30',
        videoFormat: '9:16',
        additionalHooks: 0,
        additionalCTAs: 0
      });
      setScriptLibrary([]); // Clear script library for new script
      setActiveScriptIdx(0);
      setHookRatings({}); // Clear hook ratings
      setRatingHookIndex(null); // Clear rating loading state
      setIsCreatingNewScript(true); // Mark as creating new script
      
      // Auto-select campaign if initialCampaign is provided
      if (initialCampaign) {
        console.log('Auto-selecting campaign:', initialCampaign);
        setSelectedCampaign(initialCampaign);
      }
      
      // If initialBrief is provided, pre-select it but start from step 1
      if (initialBrief) {
        console.log('Pre-selecting brief:', initialBrief);
        setSelectedBriefId(initialBrief.id);
        setSelectedBrief(initialBrief);
      }
      setStep(1); // Always start from the beginning to choose AI/Manual
      setMode('');
    }
  }, [script, open, initialBrief, initialCampaign]);

  // Clear script data when panel closes
  useEffect(() => {
    if (!open) {
      console.log('Script panel closed - clearing all data');
      setScriptLibrary([]);
      setActiveScriptIdx(0);
      setHookRatings({});
      setRatingHookIndex(null);
      setIsCreatingNewScript(true);
      setError('');
      setSuccessMessage('');
      setShowSuccess(false);
    }
  }, [open]);

  // Add function to count extra hooks and CTAs
  const updateExtraCounts = (lines) => {
    let hooks = 0;
    let ctas = 0;
    
    lines.forEach((line, idx) => {
      if (line.type === 'hook' && idx !== 0) {
        hooks++;
      } else if (line.type === 'cta' && idx !== lines.length - 1) {
        ctas++;
      }
    });
    
    setExtraHooksCount(hooks);
    setExtraCTAsCount(ctas);
  };

  // Update useEffect to initialize counts when script library changes
  useEffect(() => {
    if (scriptLibrary[activeScriptIdx]?.lines) {
      updateExtraCounts(scriptLibrary[activeScriptIdx].lines);
    }
  }, [scriptLibrary, activeScriptIdx]);

  // Auto-adjust body lines based on video duration when editing
  // TEMPORARILY DISABLED - Will be re-enabled once Python backend generates correct number of body lines
  // useEffect(() => {
  //   if (step !== 5 || !scriptLibrary[activeScriptIdx]?.lines) return;
    
  //   const duration = parseInt(scriptSettings.videoLength) || 30;
  //   const targetBodyLines = getBodyLineCount(duration);
    
  //   const lines = scriptLibrary[activeScriptIdx].lines;
  //   const currentBodyLines = lines.filter(l => l.type === 'line').length;
    
  //   console.log('Auto-adjust check:', { duration, targetBodyLines, currentBodyLines });
    
  //   if (currentBodyLines !== targetBodyLines) {
  //     console.log('Adjusting body lines from', currentBodyLines, 'to', targetBodyLines);
  //     const hook = lines.find(l => l.type === 'hook');
  //     const cta = lines.find(l => l.type === 'cta');
  //     const existingBodyLines = lines.filter(l => l.type === 'line');
      
  //     // Trim or add body lines to match target
  //     let adjustedBodyLines = [...existingBodyLines];
  //     if (adjustedBodyLines.length > targetBodyLines) {
  //       adjustedBodyLines = adjustedBodyLines.slice(0, targetBodyLines);
  //     } else if (adjustedBodyLines.length < targetBodyLines) {
  //       const needed = targetBodyLines - adjustedBodyLines.length;
  //       for (let i = 0; i < needed; i++) {
  //         adjustedBodyLines.push({ type: 'line', text: '', action: '', notes: '' });
  //       }
  //     }
      
  //     // Reconstruct lines array
  //     const newLines = [];
  //     if (hook) newLines.push(hook);
  //     newLines.push(...adjustedBodyLines);
  //     if (cta) newLines.push(cta);
      
  //     console.log('New lines structure:', newLines);
      
  //     setScriptLibrary(prev => prev.map((script, idx) =>
  //       idx === activeScriptIdx ? { ...script, lines: newLines } : script
  //     ));
  //   }
  // }, [scriptSettings.videoLength, step]); // Only trigger on duration change in editor step

  const handleCampaignChange = (e) => {
    setSelectedCampaign(e.target.value);
    setSelectedBriefId('');
    setSelectedBrief(null);
  };

  const handleBriefSelect = (e) => {
    setSelectedBriefId(e.target.value);
  };



  const handleAddScriptLine = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: (() => {
              const lines = script.lines;
              // Find the last hook index
              const lastHookIndex = lines.findLastIndex(line => line.type === 'hook');
              // Find the first CTA index
              const firstCTAIndex = lines.findIndex(line => line.type === 'cta');
              
              // If there are CTAs, insert before the first CTA
              if (firstCTAIndex !== -1) {
                return [
                  ...lines.slice(0, firstCTAIndex),
                  { type: 'line', text: '', action: '', notes: '' },
                  ...lines.slice(firstCTAIndex)
                ];
              }
              // Otherwise, add at the end
              return [...lines, { type: 'line', text: '', action: '', notes: '' }];
            })()
          }
        : script
    ));
  };

  const handleAddHook = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: [
              { type: 'hook', text: '', notes: '', example: null },
              ...script.lines
            ]
          }
        : script
    ));
  };

  const handleAddCTA = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: [
              ...script.lines,
              { type: 'cta', text: '', notes: '', example: null }
            ]
          }
        : script
    ));
  };

  const handleScriptNameChange = (value) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx ? { ...script, name: value } : script
    ));
  };

  const handleRemoveScript = (idx) => {
    if (scriptLibrary.length === 1) return;
    setScriptLibrary(scripts => scripts.filter((_, i) => i !== idx));
    setActiveScriptIdx(idx === 0 ? 0 : idx - 1);
  };

  const handleScriptLineChange = (idx, field, value) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? { ...script, lines: script.lines.map((line, i) => i === idx ? { ...line, [field]: value } : line) }
        : script
    ));
  };

  const handleRemoveScriptLine = (idx) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) => {
      if (sIdx === activeScriptIdx) {
        const removedLine = script.lines[idx];
        const newLines = script.lines.filter((_, i) => i !== idx);

        let updatedHooks = scriptSettings.additionalHooks;
        let updatedCTAs = scriptSettings.additionalCTAs;

        if (removedLine?.type === 'hook') {
          const currentHooksAfterRemoval = newLines.filter(line => line.type === 'hook').length;
          const targetHooks = 1 + updatedHooks;
          if (currentHooksAfterRemoval < targetHooks) {
            updatedHooks = Math.max(0, currentHooksAfterRemoval - 1);
          }
        } else if (removedLine?.type === 'cta') {
          const currentCTAsAfterRemoval = newLines.filter(line => line.type === 'cta').length;
          const targetCTAs = 1 + updatedCTAs;
          if (currentCTAsAfterRemoval < targetCTAs) {
            updatedCTAs = Math.max(0, currentCTAsAfterRemoval - 1);
          }
        }

        setScriptSettings(prev => ({
          ...prev,
          additionalHooks: updatedHooks,
          additionalCTAs: updatedCTAs
        }));

        return { ...script, lines: newLines };
      }
      return script;
    }));
  };

  // Hook rating function
  const handleRateHook = async (lineIndex) => {
    if (mode !== 'manual') return; // Only available in manual mode
    
    const currentScript = scriptLibrary[activeScriptIdx];
    if (!currentScript || !currentScript.lines[lineIndex]) return;
    
    const hookLine = currentScript.lines[lineIndex];
    if (hookLine.type !== 'hook' || !hookLine.text.trim()) {
      setError('Please enter hook text before rating');
      return;
    }
    
    setRatingHookIndex(lineIndex);
    setError('');
    
    try {
      const { rateHook } = await import('../services/apiService');
      
      const ratingData = {
        hookText: hookLine.text.trim(),
        platform: scriptSettings.platform || 'tiktok',
        targetAudience: selectedBrief?.audience || ''
      };
      
      const result = await rateHook(ratingData);
      
      if (result.success) {
        const rawRating = result.result.rating || {};
        const originalScore = Number.isFinite(rawRating.score) ? rawRating.score : 0;
        const adjustedScore = Math.min(10, Math.max(0, Math.round(originalScore + 1.5)));
        const isExcellent = adjustedScore >= 9;

        const adjustedRating = {
          score: adjustedScore,
          explanation: isExcellent
            ? 'Your hook already feels compelling and attention-grabbing.'
            : rawRating.explanation || 'Here is some guidance to strengthen this hook further.',
          suggestions: isExcellent ? [] : Array.isArray(rawRating.suggestions) ? rawRating.suggestions : [],
          improved_example: isExcellent ? '' : (rawRating.improved_example || '')
        };

        setHookRatings(prev => ({
          ...prev,
          [lineIndex]: adjustedRating
        }));
      } else {
        setError('Failed to rate hook: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Hook rating error:', err);
      setError('Failed to rate hook: ' + err.message);
    } finally {
      setRatingHookIndex(null);
    }
  };

  // Generate hook function
  const handleGenerateHook = async (lineIndex) => {
    const currentScript = scriptLibrary[activeScriptIdx];
    if (!currentScript || !currentScript.lines[lineIndex]) return;
    
    const hookLine = currentScript.lines[lineIndex];
    if (hookLine.type !== 'hook') {
      setError('This function is only available for hook sections');
      return;
    }
    
    // Set a local loading state for this specific hook instead of global loading
    setRegeneratingLineIndex(lineIndex);
    setRegeneratingLineType('hook');
    setError('');
    
    try {
      const { generateHook } = await import('../services/apiService');
      
      const hookData = {
        product: selectedBrief?.product || '',
        audience: selectedBrief?.audience || '',
        goal: selectedBrief?.goal || '',
        tone: selectedBrief?.tone || '',
        platform: scriptSettings.platform || 'tiktok',
        campaignGoal: selectedCampaign?.primaryGoal || 'views'
      };
      
      const result = await generateHook(hookData);
      
      if (result.success) {
        // Update the hook with generated content
        const updatedScripts = [...scriptLibrary];
        updatedScripts[activeScriptIdx] = {
          ...currentScript,
          lines: currentScript.lines.map((line, idx) => 
            idx === lineIndex 
              ? {
                  ...line,
                  text: result.result.script || line.text,
                  action: result.result.action || line.action,
                  notes: result.result.notes || line.notes
                }
              : line
          )
        };
        setScriptLibrary(updatedScripts);
      } else {
        setError('Failed to generate hook: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Hook generation error:', err);
      setError('Failed to generate hook: ' + err.message);
    } finally {
      setRegeneratingLineIndex(null);
      setRegeneratingLineType(null);
    }
  };

  // AI Enhance content function
  const handleEnhanceContent = async (lineIndex) => {
    const currentScript = scriptLibrary[activeScriptIdx];
    if (!currentScript || !currentScript.lines[lineIndex]) return;
    
    const contentLine = currentScript.lines[lineIndex];
    if (contentLine.type !== 'line' && contentLine.type !== 'cta') {
      setError('This function is only available for body lines and CTAs');
      return;
    }
    
    // Set a local loading state for this specific content
    setRegeneratingLineIndex(lineIndex);
    setRegeneratingLineType(contentLine.type);
    setError('');
    
    try {
      const { enhanceContent } = await import('../services/apiService');
      
      const enhanceData = {
        content: contentLine.text || '',
        contentType: contentLine.type, // 'line' or 'cta'
        product: selectedBrief?.product || '',
        audience: selectedBrief?.audience || '',
        goal: selectedBrief?.goal || '',
        tone: selectedBrief?.tone || '',
        platform: scriptSettings.platform || 'tiktok',
        campaignGoal: selectedCampaign?.primaryGoal || 'views'
      };
      
      const result = await enhanceContent(enhanceData);
      
      if (result.success) {
        // Update the content with enhanced version
        const updatedScripts = [...scriptLibrary];
        updatedScripts[activeScriptIdx] = {
          ...currentScript,
          lines: currentScript.lines.map((line, idx) => 
            idx === lineIndex 
              ? {
                  ...line,
                  text: result.result.enhancedScript || line.text,
                  action: result.result.enhancedAction || line.action,
                  notes: result.result.enhancedNotes || line.notes
                }
              : line
          )
        };
        setScriptLibrary(updatedScripts);
      } else {
        setError('Failed to enhance content: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Content enhancement error:', err);
      setError('Failed to enhance content: ' + err.message);
    } finally {
      setRegeneratingLineIndex(null);
      setRegeneratingLineType(null);
    }
  };

  async function handleCreateScriptWithAI() {
    setIsLoading(true);
    setError('');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to generate a script');
      }

      // Get brief data for context
      const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', selectedBriefId);
      const briefDoc = await getDoc(briefRef);
      const briefData = briefDoc.exists() ? briefDoc.data() : {};

      // Extract briefSettings from the brief (if it exists)
      const briefSettingsFromBrief = briefData.briefSettings || {};
      
      // Use briefSettings from the brief if available, otherwise use scriptSettings from the panel
      const effectiveBriefSettings = Object.keys(briefSettingsFromBrief).length > 0 
        ? briefSettingsFromBrief 
        : {
            platform: scriptSettings.platform,
            creativeControl: scriptSettings.creativeControl,
            videoLength: scriptSettings.videoLength,
            videoFormat: scriptSettings.videoFormat,
            additionalHooks: scriptSettings.additionalHooks,
            additionalCTAs: scriptSettings.additionalCTAs,
            aiScriptStyle: aiScriptStyle
          };

      // Fetch campaign data to get primaryGoal
      let campaignGoal = 'views'; // Default fallback
      try {
        const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign);
        const campaignDoc = await getDoc(campaignRef);
        if (campaignDoc.exists()) {
          campaignGoal = campaignDoc.data().primaryGoal || 'views';
        }
      } catch (error) {
        console.warn('Could not fetch campaign goal, using default:', error);
      }

      // Prepare script generation data
      const scriptGenerationData = {
        userId: user.uid,
        campaignId: selectedCampaign,
        briefId: selectedBriefId,
        scriptName: newScriptName || `Script ${scriptLibrary.length + 1}`,
        scriptType: 'video', // Required by Python backend
        campaignGoal: campaignGoal, // Add campaign goal for CTA customization
        scriptSettings: {
          platform: scriptSettings.platform,
          creativeControl: scriptSettings.creativeControl,
          videoLength: scriptSettings.videoLength,
          videoFormat: scriptSettings.videoFormat,
          additionalHooks: scriptSettings.additionalHooks,
          additionalCTAs: scriptSettings.additionalCTAs,
          aiScriptStyle: aiScriptStyle,
          productType: scriptSettings.productType,
          deliveryTime: scriptSettings.deliveryTime
        },
        briefContext: {
          // Top-level brief fields
          product: briefData.product || briefData.prompt || '',
          goal: briefData.goal || briefData.campaign_goals || '',
          audience: briefData.audience || briefData.aiResponse || '',
          message: briefData.message || '',
          tone: briefData.brandToneStyle || briefData.tone || '',
          videoStyle: briefData.videoStyle || '',
          requirements: briefData.requirements || '',
          brand: briefData.brand || '',
          brandToneStyle: briefData.brandToneStyle || '',
          competitor_brands: briefData.competitor_brands || '',
          campaignName: briefData.campaignName || briefData.title || '',
          // Include briefSettings as a nested object
          briefSettings: effectiveBriefSettings,
          // Other fields
          aiResponse: briefData.aiResponse || '',
          manualBriefSections: briefData.manualBriefSections || {},
          mode: briefData.mode || 'ai'
        },
        selectedTrendingVideo: selectedTrendingVideo || null,
        // Send the full brief object with updated briefSettings so Python can use it directly
        fullBrief: {
          ...briefData,
          briefSettings: effectiveBriefSettings
        }
      };

      console.log('Sending script generation data:', JSON.stringify(scriptGenerationData, null, 2));
      
      let scriptResult;
      
      // Try to use the API service first, fallback to mock if it fails
      try {
        const { generateScript } = await import('../services/apiService');
        // Get the Firebase auth token to pass to the API
        const idToken = await user.getIdToken();
        scriptResult = await generateScript(scriptGenerationData, idToken);
      } catch (apiError) {
        console.log('API call failed, using mock generation:', apiError);
        scriptResult = await generateMockScript(scriptGenerationData);
      }

      if (scriptResult && scriptResult.result) {
        // Debug: log the raw result
        console.log('Raw script result:', scriptResult.result);
        let parsedLines = parseScriptContent(scriptResult.result);
        if (scriptSettings.creativeControl === 'some') {
          const contextTips = buildContextTips(parsedLines, briefData);
          parsedLines = applyCreativeFlairInstructions({ parsedLines, contextTips, campaignGoal });
        }
        console.log('Parsed lines:', parsedLines);
        console.log('First line details:', parsedLines[0]);
        console.log('First line keys:', Object.keys(parsedLines[0] || {}));
        
        // Create the new script
        const newScript = {
          id: Date.now(),
          name: newScriptName || `Script ${scriptLibrary.length + 1}`,
          lines: parsedLines,
          settings: scriptSettings,
          assignedBriefIds: [selectedBriefId],
          origin: 'ai', // Mark as AI-generated
          createdAt: new Date().toISOString()
        };
        
        // Add to script library and set the active index based on the new length
        setScriptLibrary(prev => {
          const updated = [...prev, newScript];
          setActiveScriptIdx(updated.length - 1); // Set to the index of the newly added script
          return updated;
        });
        
        // Don't save to Firestore here - only save when user clicks "Save Script" in step 5
        // This prevents duplicate scripts from being created
        
        setStep(5); // Move to script editor
      } else {
        setError('No script generated');
      }
    } catch (err) {
      console.error('Script generation error:', err);
      setError('Failed to generate script: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // Enhanced mock script generation function
  async function generateMockScript(scriptData) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    const { scriptSettings, briefContext, selectedTrendingVideo } = scriptData;
    
    // If we have a trending video with a style prompt, incorporate it
    const stylePrompt = selectedTrendingVideo?.stylePrompt || null;
    
    // Generate context-aware script content
    let scriptContent = '';
    let sectionNumber = 1;
    
    // Generate hooks based on style and context
    const totalHooks = 1 + scriptSettings.additionalHooks;
    
    let hookStyles, selectedHookStyle;
    
    // Use style prompt if available
    if (stylePrompt) {
      // Extract hook pattern from style prompt
      const hookMatch = stylePrompt.match(/HOOK PATTERN:([\s\S]*?)(?=BODY|VISUAL|CALL-TO-ACTION|PACING|$)/i);
      const hookPatternText = hookMatch ? hookMatch[1].trim() : '';
      
      // If we find hook examples, use them
      if (hookPatternText.includes('*')) {
        const hookLines = hookPatternText.split('*').filter(line => line.trim());
        selectedHookStyle = hookLines.map(line => line.trim());
      } else {
        // Fallback to general hook styles
        hookStyles = {
          'trend': [
            "Hey TikTok! 👋 You won't believe what I just discovered...",
            "Stop scrolling! This is the game-changer you've been waiting for!",
            "POV: When you finally find that one product that actually works..."
          ],
          'competitive': [
            "Forget everything you thought you knew about [product category]...",
            "I've tried them all, and nothing compares to this...",
            "The secret that [competitor] doesn't want you to know..."
          ],
          'viral': [
            "This is going viral for a reason! 🔥",
            "I can't stop thinking about this...",
            "You need to see this before everyone else does!"
          ]
        };
        selectedHookStyle = hookStyles[aiScriptStyle] || hookStyles['trend'];
      }
    } else {
      // Use default hook styles
      hookStyles = {
        'trend': [
          "Hey TikTok! 👋 You won't believe what I just discovered...",
          "Stop scrolling! This is the game-changer you've been waiting for!",
          "POV: When you finally find that one product that actually works..."
        ],
        'competitive': [
          "Forget everything you thought you knew about [product category]...",
          "I've tried them all, and nothing compares to this...",
          "The secret that [competitor] doesn't want you to know..."
        ],
        'viral': [
          "This is going viral for a reason! 🔥",
          "I can't stop thinking about this...",
          "You need to see this before everyone else does!"
        ]
      };
      selectedHookStyle = hookStyles[aiScriptStyle] || hookStyles['trend'];
    }
    
    for (let i = 0; i < totalHooks; i++) {
      let hookText = selectedHookStyle[i % selectedHookStyle.length];
      
      // Replace placeholders
      hookText = hookText
        .replace('[product category]', briefContext.product || 'this product')
        .replace('[competitor]', 'other brands')
        .replace('[product]', briefContext.product || 'this');
        
      scriptContent += `${sectionNumber}. HOOK${totalHooks > 1 ? ` ${i + 1}` : ''}
- Script: ${hookText}
- Action: ${i === 0 ? "Show product with excitement and energy" : "Show different angle or approach"}
- Notes: ${i === 0 ? "Start with energy and curiosity - grab attention in first 3 seconds" : "Keep audience engaged with different hook approach"}

`;
      sectionNumber++;
    }
    
    // Generate body content based on brief context or style prompt
    let bodyText;
    if (stylePrompt) {
      bodyText = `Using the ${selectedTrendingVideo.title} style to showcase ${briefContext.product || 'this product'}`;
    } else {
      const bodyScripts = [
        `Let me show you exactly why ${briefContext.product || 'this product'} is a game-changer!`,
        `I've been using this for ${briefContext.videoStyle || 'a while now'}, and the results are incredible.`,
        `Here's what makes ${briefContext.product || 'this'} so special...`,
        `The best part? It actually delivers on its promises!`
      ];
      bodyText = bodyScripts[Math.floor(Math.random() * bodyScripts.length)];
    }
    
    scriptContent += `${sectionNumber}. BODY LINE
- Script: ${bodyText}
- Action: Demonstrate key features and benefits
- Notes: Focus on main benefits - keep it authentic and relatable

`;
    sectionNumber++;
    
    // Generate CTAs based on style
    const totalCTAs = 1 + scriptSettings.additionalCTAs;
    const ctaStyles = {
      'trend': [
        "Don't wait - try it yourself and see the difference! 🔥",
        "Trust me, you won't regret this! Drop a ❤️ if you're convinced!"
      ],
      'competitive': [
        "Stop settling for less. Get the real deal now!",
        "Why choose anything else when you can have the best?"
      ],
      'viral': [
        "This is going viral for a reason! Get yours before it sells out!",
        "Share this with someone who needs to see it! 🚀"
      ]
    };

    const selectedCtaStyle = ctaStyles[aiScriptStyle] || ctaStyles['trend'];
    
    for (let i = 0; i < totalCTAs; i++) {
      const ctaText = selectedCtaStyle[i % selectedCtaStyle.length];
      
      scriptContent += `${sectionNumber}. CTA${totalCTAs > 1 ? ` ${i + 1}` : ''}
- Script: ${ctaText}
- Action: ${i === 0 ? "Show call-to-action with urgency" : "Show different CTA approach"}
- Notes: ${i === 0 ? "Create urgency and excitement - make it feel exclusive" : "Reinforce the message and drive action"}

`;
      sectionNumber++;
    }
    
    return {
      result: scriptContent.trim()
    };
  }

  // Function to generate mock regenerated content for a specific section
  async function generateMockRegeneratedSection(sectionData) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const { sectionType, sectionIndex, briefContext } = sectionData;
    
    // Generate different content based on section type and context
    const sectionGenerators = {
      hook: () => {
        const hookVariations = [
          {
            text: `Hey ${briefContext.audience || 'everyone'}! 👋 You won't believe what I just discovered about ${briefContext.product || 'this product'}...`,
            action: "Show product with excitement and energy",
            notes: "Start with energy and curiosity - grab attention in first 3 seconds"
          },
          {
            text: `Stop scrolling! This ${briefContext.product || 'product'} is the game-changer you've been waiting for!`,
            action: "Show product with urgency and excitement",
            notes: "Create immediate interest and stop the scroll"
          },
          {
            text: `POV: When you finally find that one ${briefContext.product || 'product'} that actually works...`,
            action: "Show relatable moment with product",
            notes: "Make it feel personal and relatable"
          },
          {
            text: `I've been using this ${briefContext.product || 'product'} for ${briefContext.videoStyle || 'a while now'}, and the results are incredible!`,
            action: "Show product with confidence and results",
            notes: "Build credibility and show results"
          }
        ];
        return hookVariations[sectionIndex % hookVariations.length];
      },
      line: () => {
        const bodyVariations = [
          {
            text: `Let me show you exactly why ${briefContext.product || 'this product'} is a game-changer!`,
            action: "Demonstrate key features and benefits",
            notes: "Focus on main benefits - keep it authentic and relatable"
          },
          {
            text: `Here's what makes ${briefContext.product || 'this'} so special...`,
            action: "Show unique features and value proposition",
            notes: "Highlight what sets this apart from others"
          },
          {
            text: `The best part? It actually delivers on its promises!`,
            action: "Show proof points and results",
            notes: "Build trust and credibility"
          },
          {
            text: `I can't believe I went so long without this ${briefContext.product || 'product'}!`,
            action: "Show transformation or before/after",
            notes: "Create emotional connection and desire"
          }
        ];
        return bodyVariations[Math.floor(Math.random() * bodyVariations.length)];
      },
      cta: () => {
        const ctaVariations = [
          {
            text: `Don't wait - try ${briefContext.product || 'it'} yourself and see the difference! 🔥`,
            action: "Show call-to-action with urgency",
            notes: "Create urgency and excitement - make it feel exclusive"
          },
          {
            text: `Trust me, you won't regret this! Drop a ❤️ if you're convinced!`,
            action: "Show social proof and engagement",
            notes: "Encourage interaction and build community"
          },
          {
            text: `This is going viral for a reason! Get yours before it sells out!`,
            action: "Show scarcity and exclusivity",
            notes: "Create FOMO and drive immediate action"
          },
          {
            text: `Share this with someone who needs to see it! 🚀`,
            action: "Show sharing and virality",
            notes: "Encourage sharing and organic reach"
          }
        ];
        return ctaVariations[sectionIndex % ctaVariations.length];
      }
    };

    const generator = sectionGenerators[sectionType];
    const regeneratedContent = generator ? generator() : {
      text: "Regenerated content",
      action: "Show content",
      notes: "Updated section"
    };

    return {
      result: regeneratedContent
    };
  }

  // Function to create manual script based on settings
  const handleCreateManualScript = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to create a script');
      }
      
      // Create script structure based on additional hooks and CTAs settings
      const lines = [];
      
      // Add hooks (1 + additional hooks)
      const totalHooks = 1 + scriptSettings.additionalHooks;
      for (let i = 0; i < totalHooks; i++) {
        lines.push({
          type: 'hook',
          text: '',
          action: '',
          notes: ''
        });
      }
      
      // Add body line
      lines.push({
        type: 'line',
        text: '',
        action: '',
        notes: ''
      });
      
      // Add CTAs (1 + additional CTAs)
      const totalCTAs = 1 + scriptSettings.additionalCTAs;
      for (let i = 0; i < totalCTAs; i++) {
        lines.push({
          type: 'cta',
          text: '',
          action: '',
          notes: ''
        });
      }
      
      // Create new script with the structure
      const newScript = {
        id: Date.now(),
        name: newScriptName || `Script ${scriptLibrary.length + 1}`,
        lines: lines,
        settings: scriptSettings,
        assignedBriefIds: [selectedBriefId],
        origin: 'manual', // Mark as manually created
        createdAt: new Date().toISOString()
      };
      
      // Clear hook ratings from previous script
      setHookRatings({});
      setRatingHookIndex(null);
      
      // Replace script library with new script (not add to it)
      setScriptLibrary([newScript]);
      setActiveScriptIdx(0);
      
      // Mark as no longer creating new script
      setIsCreatingNewScript(false);
      
      // Don't save to Firestore here - only save when user clicks "Save Script" in step 5
      // This prevents duplicate scripts from being created
      
      setStep(5); // Move to script editor
    } catch (err) {
      console.error('Error creating manual script:', err);
      setError('Failed to create script: ' + err.message);
    }
  };

  // Function to regenerate a specific script section
  const handleRegenerateScriptSection = async (sectionIndex) => {
    try {
      setIsLoading(true);
      setError('');
      
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to regenerate sections');
      }

      // Get brief data for context
      const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', selectedBriefId);
      const briefDoc = await getDoc(briefRef);
      const briefData = briefDoc.exists() ? briefDoc.data() : {};

      // Fetch campaign data to get primaryGoal
      let campaignGoal = 'views'; // Default fallback
      try {
        const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign);
        const campaignDoc = await getDoc(campaignRef);
        if (campaignDoc.exists()) {
          campaignGoal = campaignDoc.data().primaryGoal || 'views';
        }
      } catch (error) {
        console.warn('Could not fetch campaign goal, using default:', error);
      }

      const currentLine = scriptLibrary[activeScriptIdx].lines[sectionIndex];
      const sectionType = currentLine.type;
      
      // Use the API service for section regeneration
      const { regenerateScriptSection } = await import('../services/apiService');
      const sectionData = {
        userId: user.uid,
        campaignId: selectedCampaign,
        briefId: selectedBriefId,
        sectionType: sectionType,
        sectionIndex: sectionIndex,
        briefContext: {
          product: briefData.product || '',
          goal: briefData.goal || '',
          audience: briefData.audience || '',
          message: briefData.message || '',
          tone: briefData.tone || '',
          videoStyle: briefData.videoStyle || '',
          requirements: briefData.requirements || ''
        },
        scriptSettings: scriptSettings,
        aiScriptStyle: aiScriptStyle
      };
      
      let regeneratedResult;
      
      // Try to use the API service first, fallback to mock if it fails
      try {
        regeneratedResult = await regenerateScriptSection(sectionData);
      } catch (apiError) {
        console.log('API call failed, using mock regeneration:', apiError);
        regeneratedResult = await generateMockRegeneratedSection(sectionData);
      }
      
      const regeneratedContent = regeneratedResult.result;
      
      // Update the specific section
      const updatedLines = [...scriptLibrary[activeScriptIdx].lines];
      let updatedLine = {
        ...currentLine,
        text: regeneratedContent.text,
        action: regeneratedContent.action,
        notes: regeneratedContent.notes
      };


      if (scriptSettings.creativeControl === 'some') {
        const draftLines = [...updatedLines];
        draftLines[sectionIndex] = updatedLine;
        const contextTips = buildContextTips(draftLines, selectedBrief || {});
        
        // For body lines, use story flow based on position
        if (currentLine.type === 'line') {
          // Count how many body lines come before this one
          let bodyLineIndex = 0;
          for (let i = 0; i < sectionIndex; i++) {
            if (updatedLines[i].type === 'line') {
              bodyLineIndex++;
            }
          }
          const totalBodyLines = updatedLines.filter(l => l.type === 'line').length;
          
          const scriptLength = parseInt(scriptSettings.videoLength) || 30;
          
          // Get previous line context for flow
          const previousLine = sectionIndex > 0 ? updatedLines[sectionIndex - 1] : null;
          const previousContext = previousLine?.text ? previousLine.text.substring(0, 100) : '';
          
          // Generate script line guidance with previous context
          const scriptGuideline = generateScriptLineGuidance('line', bodyLineIndex, totalBodyLines, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, true, previousContext, campaignGoal);
          
          // Generate detailed notes and action
          const detailedNotes = generateDetailedNotes('line', contextTips.briefData || {}, bodyLineIndex, totalBodyLines, aiScriptStyle, scriptLength, campaignGoal);
          const actionGuideline = generateActionGuidelines('line', bodyLineIndex, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, true, campaignGoal);
          updatedLine = {
            ...updatedLine,
            text: scriptGuideline || updatedLine.text,
            action: actionGuideline || updatedLine.action || '',
            notes: detailedNotes
          };
        } else {
          // For hooks and CTAs, use the base guidance
          let flow = null;
          if (isTakeInspiration && selectedTrendingVideo?.stylePrompt) {
            const trendingFlow = parseTrendingVideoStylePrompt(selectedTrendingVideo.stylePrompt);
            if (trendingFlow && trendingFlow[currentLine.type]) {
              flow = trendingFlow[currentLine.type];
            }
          }
          
          // Otherwise, get style-specific guidance based on aiScriptStyle
          if (!flow) {
            const styleGuidance = creativeFlairGuidanceByStyle[aiScriptStyle] || creativeFlairGuidanceMap;
            flow = styleGuidance[currentLine.type] || creativeFlairGuidanceMap[currentLine.type];
          }
          
          if (flow) {
            // Determine if this is primary or additional hook/CTA
            let isPrimary = true;
            let additionalCount = 0;
            const totalBodyLines = updatedLines.filter(l => l.type === 'line').length;
            const scriptLength = parseInt(scriptSettings.videoLength) || 30;
            
            if (currentLine.type === 'hook') {
              // Count how many hooks come before this one
              let hookCount = 0;
              for (let i = 0; i < sectionIndex; i++) {
                if (updatedLines[i].type === 'hook') hookCount++;
              }
              isPrimary = hookCount === 0;
              additionalCount = scriptSettings.additionalHooks || 0;
              
              // Generate script guideline instead of using flow directly
              const previousLine = sectionIndex > 0 ? updatedLines[sectionIndex - 1] : null;
              const previousContext = previousLine?.text ? previousLine.text.substring(0, 100) : '';
              const scriptGuideline = generateScriptLineGuidance('hook', hookCount, updatedLines.filter(l => l.type === 'hook').length, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimary, previousContext, campaignGoal);
              const actionGuideline = generateActionGuidelines('hook', hookCount, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimary, campaignGoal);
              updatedLine = {
                ...updatedLine,
                text: scriptGuideline || flow,
                action: actionGuideline || updatedLine.action || '',
                notes: generateDetailedNotes('hook', contextTips.briefData || {}, 0, 1, aiScriptStyle, scriptLength, campaignGoal)
              };
            } else if (currentLine.type === 'cta') {
              // Count how many CTAs come after this one
              let ctaCount = 0;
              for (let i = sectionIndex + 1; i < updatedLines.length; i++) {
                if (updatedLines[i].type === 'cta') ctaCount++;
              }
              isPrimary = ctaCount === 0; // Last CTA is primary
              additionalCount = scriptSettings.additionalCTAs || 0;
              
              // Count total CTAs
              const totalCTAs = updatedLines.filter(l => l.type === 'cta').length;
              const ctaIndex = totalCTAs - ctaCount - 1;
              
              // Generate script guideline instead of using flow directly
              const previousLine = sectionIndex > 0 ? updatedLines[sectionIndex - 1] : null;
              const previousContext = previousLine?.text ? previousLine.text.substring(0, 100) : '';
              const scriptGuideline = generateScriptLineGuidance('cta', ctaIndex, totalCTAs, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimary, previousContext, campaignGoal);
              const actionGuideline = generateActionGuidelines('cta', ctaIndex, totalBodyLines, aiScriptStyle, contextTips.briefData || {}, scriptLength, isPrimary, campaignGoal);
              updatedLine = {
                ...updatedLine,
                text: scriptGuideline || flow,
                action: actionGuideline || updatedLine.action || '',
                notes: generateDetailedNotes('cta', contextTips.briefData || {}, 0, 1, aiScriptStyle, scriptLength, campaignGoal)
              };
            }
          }
        }
      }

      updatedLines[sectionIndex] = updatedLine;
      
      setScriptLibrary(prev => prev.map((script, idx) => 
        idx === activeScriptIdx ? { ...script, lines: updatedLines } : script
      ));
      
    } catch (error) {
      console.error('Error regenerating section:', error);
      setError(`Failed to regenerate section: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };



  // Helper function to parse the AI-generated script content into script lines
  function parseScriptContent(content) {
    const lines = [];
    
    // Split content into sections using a more robust regex
    const sections = content.split(/(?=\d+\.\s+(?:HOOK|BODY LINE|CTA))/i);
    
    sections.forEach(section => {
      const trimmedSection = section.trim();
      if (!trimmedSection) return;
      
      // Extract section header to determine type
      const headerMatch = trimmedSection.match(/^(\d+)\.\s*(HOOK|BODY LINE|CTA)/i);
      if (!headerMatch) return;
      
      const sectionNumber = headerMatch[1];
      const sectionType = headerMatch[2].toUpperCase();
      
      // Parse the section content
      const fields = {};
      trimmedSection.split('\n').forEach(line => {
        const fieldMatch = line.match(/^-\s*(Script|Action|Notes):\s*(.*)$/i);
        if (fieldMatch) {
          // Remove surrounding quotes if present
          let value = fieldMatch[2].trim();
          if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
            value = value.slice(1, -1);
          }
          fields[fieldMatch[1].toLowerCase()] = value;
        }
      });
      
      if (Object.keys(fields).length > 0) {
        // Convert 'script' key to 'text' for consistency with the UI
        const normalizedFields = { ...fields };
        if (normalizedFields.script) {
          normalizedFields.text = normalizedFields.script;
          delete normalizedFields.script;
        }
        
        // Determine type based on section header
        let type;
        if (sectionType === 'HOOK') {
          type = 'hook';
        } else if (sectionType === 'CTA') {
          type = 'cta';
        } else if (sectionType === 'BODY LINE') {
          type = 'line';
        } else {
          // Fallback for any other section types
          type = 'line';
        }
        
        lines.push({ type, ...normalizedFields });
      }
    });
    
    return lines;
  }

  const handleSaveScript = async (isDraft = false) => {
    setError('');
    setSaving(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const uid = user?.uid;
      if (!uid) throw new Error('You must be logged in to save.');
      if (!selectedCampaign) throw new Error('Please select a campaign.');
      if (!selectedBriefId) throw new Error('Please select a brief.');
      if (!newScriptName || newScriptName.trim() === '') throw new Error('Please enter a script name.');


      const briefRef = doc(db, 'users', uid, 'campaigns', selectedCampaign, 'briefs', selectedBriefId);

      // Get current brief data
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }

      const currentBrief = briefDoc.data();
      const currentScripts = currentBrief.scripts || [];
      
      let updatedScript;
      let updatedScripts;
      
      const currentScriptLines = scriptLibrary[activeScriptIdx]?.lines || initialScriptLines;
      
      console.log('currentScriptLines:', currentScriptLines);
      
      // Get origin from scriptLibrary if available, otherwise from script prop
      const scriptOrigin = scriptLibrary[activeScriptIdx]?.origin || script?.origin || 'manual';
      
      if (script) {
        // Editing existing script - preserve assignedBriefIds, origin, and update other fields
        updatedScript = {
          ...script,
          name: newScriptName,
          lines: currentScriptLines,
          settings: scriptSettings,
          origin: scriptOrigin, // IMPORTANT: preserve origin
          isDraft: isDraft,
          updatedAt: new Date().toISOString()
        };
        
        // Update the existing script in the array
        updatedScripts = currentScripts.map(s => s.id === script.id ? updatedScript : s);
      } else {
        // Creating new script - use origin from scriptLibrary
        updatedScript = {
          id: Date.now(),
          name: newScriptName,
          lines: currentScriptLines,
          settings: scriptSettings,
          assignedBriefIds: [selectedBriefId], // Track which briefs this script is assigned to
          origin: scriptOrigin, // IMPORTANT: preserve origin from scriptLibrary
          isDraft: isDraft,
          createdAt: new Date().toISOString()
        };
        
        updatedScripts = [...currentScripts, updatedScript];
      }
      

      // Helper function to recursively remove undefined values
      const removeUndefined = (obj) => {
        if (obj === null || obj === undefined) {
          return null;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item));
        }
        
        if (typeof obj === 'object') {
          const cleaned = {};
          Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          });
          return cleaned;
        }
        
        return obj;
      };

      // Filter out undefined values from scripts before saving to Firestore
      const cleanScripts = removeUndefined(updatedScripts);

      // Update Firestore
      await updateDoc(briefRef, {
        scripts: cleanScripts,
        updatedAt: new Date().toISOString()
      });

      // Call callback
      if (onScriptSaved) {
        onScriptSaved({
          campaign: selectedCampaign,
          brief: selectedBriefId,
          script: updatedScript
        });
      }

      setSuccessMessage(isDraft ? 'Draft saved successfully!' : 'Script saved successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 2000);
      onClose();
    } catch (err) {
      console.error('Error saving script:', err);
      setError('Failed to save script: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  function formatSeconds(seconds) {
    seconds = parseInt(seconds, 10);
    if (seconds < 60) return `${seconds}s`;
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  function getVideoLengthPrompt(seconds) {
    seconds = parseInt(seconds, 10);
    if (seconds <= 35) {
      return 'Great for capturing attention. Ideal for platforms like TikTok, Insta Reels, and YouTube Shorts, and keeping viewers engaged.';
    } else if (seconds <= 60) {
      return 'Good for providing more context. Suitable for Instagram and Facebook ads.';
    } else {
      return 'Ideal for longer-form content like YouTube, explainers, and in-depth storytelling.';
    }
  }

  if (!open) return null;

  // If forceMode is set and we're starting past step 1, skip method selection
  const shouldShowMethodSelection = !forceMode && step === 1;

  return (
    <div className="script-panel-overlay">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="script-loading-overlay">
          <div className="script-loading-content">
            <img src="/ugc3-logo.png" alt="UGC3" className="script-loading-logo" />
            <div className="script-loading-spinner"></div>
            <h3>Generating Your Script</h3>
            <p className="loading-message">{scriptLoadingSteps[loadingStep]}</p>
          </div>
        </div>
      )}
      
      <div ref={panelRef} className="script-panel-fullscreen">
        {/* Top Header/Fold */}
        <div className="script-panel-header">
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {/* Step 1: Creation Method Selection */}
        {shouldShowMethodSelection && (
          <div className="step-content creation-method-step">
            <div className="step-header">
              <h2>Choose Your Method</h2>
              <p>Select how you'd like to create your UGC script</p>
            </div>
            
            <div className="creation-method-cards">
              <button 
                className={`creation-method-card ${mode === 'ai' ? 'selected' : ''}`}
                onClick={() => setMode('ai')}
              >
                <div className="method-icon">
                  <div className="method-icon-stack">
                    <img src="/ugc3-logo.png" alt="UGC3" className="method-logo-small" />
                    <Bot size={32} className="method-ai-icon" />
                  </div>
                </div>
                <div className="method-content">
                  <h3>Script Assistant</h3>
                  <p>Let our AI assistant help you generate a professional script based on your brief and settings. Perfect for quick, high-quality scripts.</p>
                  <div className="ai-brief-badge">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                    </svg>
                    Smart & Fast
                  </div>
                </div>
              </button>
              
              <button 
                className={`creation-method-card ${mode === 'manual' ? 'selected' : ''}`}
                onClick={() => setMode('manual')}
              >
                <div className="method-icon">
                  <div className="method-icon-stack">
                    <div className="method-logo-placeholder"></div>
                    <PenTool size={32} className="method-manual-icon" />
                  </div>
                </div>
                <div className="method-content">
                  <h3>Create Manually</h3>
                  <p>Write your script from scratch with full control. Ideal for custom requirements and specific needs.</p>
                  <div className="manual-control-badge">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                    </svg>
                    Custom & Precise
                  </div>
                </div>
              </button>
            </div>
            
            {mode && (
              <div className="step-actions">
                <button 
                  className="btn btn-primary continue-btn"
                  onClick={() => setStep(2)}
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Name Script */}
        {step === 2 && (
          <div className="step-content script-name-step">
            <div className="step-header">
              <h2>Name Your Script</h2>
              <p>Give your script a descriptive name</p>
            </div>
            
            <div className="setup-form">
              <div className="form-group">
                <label htmlFor="script-name">Script Name</label>
                <input
                  id="script-name"
                  type="text"
                  className="form-input"
                  placeholder="Enter script name (e.g. TikTok Ad #1, Product Demo, etc.)"
                  value={newScriptName}
                  onChange={e => setNewScriptName(e.target.value)}
                  maxLength={60}
                />
                <div className="form-help">
                  This script name is visible to assigned creators.
                </div>
              </div>
            </div>
            
            <div className="step-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
              {newScriptName.trim() && (
                <button
                  className="btn btn-primary"
                  onClick={() => setStep(3)}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Brief Selection */}
        {step === 3 && (
          <div className="step-content brief-selection-step">
            <div className="step-header">
              <h2>Select a Brief</h2>
              <p>Choose which brief you want to create a script for</p>
            </div>
            
            <div className="setup-form">
              <div className="form-group">
                <label htmlFor="campaign-select">Campaign</label>
                <select
                  id="campaign-select"
                  className="form-select"
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                >
                  <option value="">Select a campaign...</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedCampaign && (
                <div className="form-group">
                  <label htmlFor="brief-select">Brief</label>
                  <select
                    id="brief-select"
                    className="form-select"
                    value={selectedBriefId}
                    onChange={handleBriefSelect}
                  >
                    <option value="">Select a brief...</option>
                    {briefsList.map(brief => (
                      <option key={brief.id} value={brief.id}>{brief.title || brief.campaignName || brief.id}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {selectedBrief && (
                <div className="selected-brief-preview">
                  <h4>Selected Brief: {selectedBrief.title}</h4>
                  <p><strong>Campaign:</strong> {campaigns.find(c => c.id === selectedCampaign)?.name || selectedCampaign}</p>
                  {selectedBrief.briefDescription && (
                    <p><strong>Description:</strong> {selectedBrief.briefDescription}</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="step-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep(2)}
              >
                ← Back
              </button>
              {selectedBriefId && (
                <button
                  className="btn btn-primary"
                  onClick={() => setStep(4)}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Script Settings */}
        {step === 4 && (
          <div className="step-content script-settings-step">
            <div className="step-header">
              <h2>Script Settings</h2>
              <p>Configure your script settings and preferences</p>
            </div>
            
            <div className="setting-group">
              <label>Product or Service</label>
              <div className="icon-grid">
                {productTypes.map(type => (
                  <div
                    key={type.value}
                    className={`icon-box ${scriptSettings.productType === type.value ? 'selected' : ''}`}
                    onClick={() => setScriptSettings(prev => ({ ...prev, productType: type.value, deliveryTime: type.value === 'service' ? 'immediate' : prev.deliveryTime }))}
                  >
                    <span className="icon">{type.icon}</span>
                    <span className="label">{type.label}</span>
                    <span className="description">{type.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {scriptSettings.productType === 'product' && (
              <div className="setting-group">
                <label>Delivery</label>
                <div className="form-help" style={{ padding: '16px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #0ea5e9' }}>
                  <strong>Product Shipping Required</strong><br />
                  When a creator accepts, you'll be provided with their shipping details to send the product. They'll confirm receipt when it arrives.
                </div>
              </div>
            )}

            {scriptSettings.productType === 'service' && (
              <div className="setting-group">
                <label>Delivery Time</label>
                <div className="form-help" style={{ padding: '16px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #0ea5e9' }}>
                  <strong>Service Available Immediately</strong><br />
                  Creators can start creating content right away without waiting for shipping.
                </div>
              </div>
            )}

            <h3>Platform & Creative Control</h3>
            <div className="platform-selection">
              <h4>Select Platform</h4>
              <div className="platform-note">(This is what the "3" in UGC3 is for — a nod to the three big platforms: Meta, TikTok, and YouTube.)</div>
              <div className="icon-grid">
                {platforms.map(platform => (
                  <div
                    key={platform.value}
                    className={`icon-box ${scriptSettings.platform === platform.value ? 'selected' : ''}`}
                    onClick={() => setScriptSettings(prev => ({ ...prev, platform: platform.value }))}
                  >
                    {platform.type === 'image' ? (
                      <img src={platform.icon} alt={platform.label} className="icon" style={{ width: '48px', height: '48px', objectFit: 'contain', display: 'block', margin: '0 auto' }} onError={(e) => { console.error('Image failed to load:', platform.icon); e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="icon">{platform.icon}</span>
                    )}
                    <span className="label">{platform.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="creative-control-selection">
              <h4>Creative Control Level</h4>
              <div className="icon-grid">
                {creativeControlOptions.map(option => (
                  <div
                    key={option.value}
                    className={`icon-box ${scriptSettings.creativeControl === option.value ? 'selected' : ''}`}
                    onClick={() => setScriptSettings(prev => ({ ...prev, creativeControl: option.value }))}
                  >
                    {option.type === 'image' ? (
                      <img src={option.icon} alt={option.label} className="icon" style={{ width: '48px', height: '48px', objectFit: 'contain', display: 'block', margin: '0 auto' }} onError={(e) => { console.error('Image failed to load:', option.icon); e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="icon">{option.icon}</span>
                    )}
                    <span className="label">{option.label}</span>
                    <span className="description">{option.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="setting-group">
              <label>Video Length</label>
              <div className="slider-box">
                <span className="icon slider-icon">⏱️</span>
                <div className="slider-label-row">
                  <span className="slider-label">Drag to set video length</span>
                  <span className="slider-value">{formatSeconds(scriptSettings.videoLength)}</span>
                </div>
                <input
                  type="range"
                  min="15"
                  max="80"
                  step="1"
                  value={scriptSettings.videoLength}
                  onChange={e => {
                    const rawValue = e.target.value;
                    const snapped = snapDuration(parseInt(rawValue));
                    setScriptSettings(prev => ({ ...prev, videoLength: snapped.toString() }));
                  }}
                  className="video-length-slider"
                />
                <div className="slider-prompt">{getVideoLengthPrompt(scriptSettings.videoLength)}</div>
                {/* Word count estimate */}
                <div className="word-count-estimate">
                  <span className="word-count-label">Estimated Word Count:</span>
                  <span className="word-count-value">{getWordRange(parseInt(scriptSettings.videoLength)).label}</span>
                </div>
              </div>
            </div>

            <div className="setting-group">
              <label>Additional Hooks</label>
              {isTakeInspiration && (
                <div style={{ 
                  padding: '8px 12px', 
                  marginBottom: '12px', 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  ⚠️ Additional hooks and CTAs are unavailable for Take Inspiration style. Any values will be reset to 0.
                </div>
              )}
              <div className="plus-minus-row">
                <button
                  className="plus-minus-btn"
                  type="button"
                  onClick={() => setScriptSettings(prev => ({ ...prev, additionalHooks: Math.max(0, prev.additionalHooks - 1) }))}
                  disabled={scriptSettings.additionalHooks <= 0 || isTakeInspiration}
                >–</button>
                <span className="plus-minus-value">{scriptSettings.additionalHooks}</span>
                <button
                  className="plus-minus-btn"
                  type="button"
                  onClick={() => setScriptSettings(prev => ({ ...prev, additionalHooks: Math.min(5, prev.additionalHooks + 1) }))}
                  disabled={scriptSettings.additionalHooks >= 5 || isTakeInspiration}
                >+</button>
              </div>
              <div className="plus-minus-charge">
                Extra charge: 0.25 credits per hook<br />
                {scriptSettings.additionalHooks > 0 && !isTakeInspiration && (
                  <span>Total: {(scriptSettings.additionalHooks * 0.25).toFixed(2)} credits</span>
                )}
                {isTakeInspiration && (
                  <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                    Unavailable for Take Inspiration style
                  </span>
                )}
              </div>
            </div>

            <div className="setting-group">
              <label>Additional CTAs</label>
              <div className="plus-minus-row">
                <button
                  className="plus-minus-btn"
                  type="button"
                  onClick={() => setScriptSettings(prev => ({ ...prev, additionalCTAs: Math.max(0, prev.additionalCTAs - 1) }))}
                  disabled={scriptSettings.additionalCTAs <= 0 || isTakeInspiration}
                >–</button>
                <span className="plus-minus-value">{scriptSettings.additionalCTAs}</span>
                <button
                  className="plus-minus-btn"
                  type="button"
                  onClick={() => setScriptSettings(prev => ({ ...prev, additionalCTAs: Math.min(5, prev.additionalCTAs + 1) }))}
                  disabled={scriptSettings.additionalCTAs >= 5 || isTakeInspiration}
                >+</button>
              </div>
              <div className="plus-minus-charge">
                Extra charge: 0.25 credits per CTA<br />
                {scriptSettings.additionalCTAs > 0 && !isTakeInspiration && (
                  <span>Total: {(scriptSettings.additionalCTAs * 0.25).toFixed(2)} credits</span>
                )}
                {isTakeInspiration && (
                  <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                    Unavailable for Take Inspiration style
                  </span>
                )}
              </div>
            </div>

            {mode === 'ai' && (
              <div className="ai-script-style-choice">
                <h3>AI Script Style</h3>
                <div className="icon-grid">
                  <div
                    className={`icon-box ${aiScriptStyle === 'trend' ? 'selected' : ''}`}
                    onClick={() => {
                      setAiScriptStyle('trend');
                      // Don't clear inspiration video when selecting trend-based
                    }}
                  >
                    <span className="icon">🔥</span>
                    <span className="label">Trend-Based UGC</span>
                    <span className="description">Use what's trending on TikTok and Reels right now.</span>
                  </div>
                  <div
                    className={`icon-box ${aiScriptStyle === 'competitive' ? 'selected' : ''}`}
                    onClick={() => {
                      setAiScriptStyle('competitive');
                      setSelectedTrendingVideo(null); // Clear inspiration when selecting other styles
                    }}
                  >
                    <span className="icon">🏆</span>
                    <span className="label">Competitive</span>
                    <span className="description">Inspired by what top competitors are doing.</span>
                  </div>
                  <div
                    className={`icon-box ${aiScriptStyle === 'viral' ? 'selected' : ''}`}
                    onClick={() => {
                      setAiScriptStyle('viral');
                      setSelectedTrendingVideo(null); // Clear inspiration when selecting other styles
                    }}
                  >
                    <span className="icon">🚀</span>
                    <span className="label">Viral</span>
                    <span className="description">Aims for viral potential and shareability.</span>
                  </div>
                </div>
                
                <div className="trending-inspiration-section">
                  <button
                    className="trending-inspiration-btn"
                    onClick={() => setShowTrendingModal(true)}
                  >
                    <span className="trending-text">Take Inspiration from This Week's Top Trending Styles</span>
                    <span className="trending-arrow">→</span>
                  </button>
                  {selectedTrendingVideo && (
                    <div className="selected-trending-video">
                      <span className="selected-text">Inspired by: {selectedTrendingVideo.title}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="step-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep(3)}
              >
                ← Back
              </button>
              {(() => {
                // Define active script and AI status
                const activeScript = scriptLibrary?.[activeScriptIdx];
                const isAiScript = activeScript?.origin === 'ai';
                
                // Debug logging
                console.log('Step 4 button logic:', {
                  isCreatingNewScript,
                  scriptLibraryLength: scriptLibrary.length,
                  activeScriptIdx,
                  hasActiveScript: !!activeScript,
                  isAiScript,
                  origin: activeScript?.origin
                });
                
                const hasScript = !isCreatingNewScript && scriptLibrary.length > 0 && activeScript;
                
                if (hasScript) {
                  // We have a script - show buttons based on origin
                  // If !isAiScript → hide Generate button
                  // If isAiScript → show Regenerate button
                  return (
                    <>
                      {isAiScript && (
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            // Regenerate script with updated settings - replace the existing script
                            try {
                              setIsLoading(true);
                              setError('');
                              
                              const auth = getAuth();
                              const user = auth.currentUser;
                              if (!user) {
                                throw new Error('You must be logged in to regenerate a script');
                              }
                              
                              const currentScript = scriptLibrary[activeScriptIdx];
                              const briefData = selectedBrief || {};
                              const effectiveBriefSettings = briefData.briefSettings || {};
                              
                              // Get campaign goal
                              let campaignGoal = briefData.goal || 'engagement';
                              if (selectedCampaign) {
                                try {
                                  const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign);
                                  const campaignDoc = await getDoc(campaignRef);
                                  if (campaignDoc.exists()) {
                                    campaignGoal = campaignDoc.data().primaryGoal || 'views';
                                  }
                                } catch (error) {
                                  console.warn('Could not fetch campaign goal, using default:', error);
                                }
                              }
                              
                              // Prepare script generation data (matching handleCreateScriptWithAI structure)
                              const scriptGenerationData = {
                                userId: user.uid,
                                campaignId: selectedCampaign,
                                briefId: selectedBriefId,
                                scriptName: currentScript.name,
                                scriptType: 'video',
                                campaignGoal: campaignGoal,
                                scriptSettings: {
                                  platform: scriptSettings.platform,
                                  creativeControl: scriptSettings.creativeControl,
                                  videoLength: scriptSettings.videoLength,
                                  videoFormat: scriptSettings.videoFormat,
                                  additionalHooks: scriptSettings.additionalHooks,
                                  additionalCTAs: scriptSettings.additionalCTAs,
                                  aiScriptStyle: aiScriptStyle,
                                  productType: scriptSettings.productType,
                                  deliveryTime: scriptSettings.deliveryTime
                                },
                                briefContext: {
                                  product: briefData.product || briefData.prompt || '',
                                  goal: briefData.goal || briefData.campaign_goals || '',
                                  audience: briefData.audience || briefData.aiResponse || '',
                                  message: briefData.message || '',
                                  tone: briefData.brandToneStyle || briefData.tone || '',
                                  videoStyle: briefData.videoStyle || '',
                                  requirements: briefData.requirements || '',
                                  brand: briefData.brand || '',
                                  brandToneStyle: briefData.brandToneStyle || '',
                                  competitor_brands: briefData.competitor_brands || '',
                                  campaignName: briefData.campaignName || briefData.title || '',
                                  briefSettings: effectiveBriefSettings,
                                  aiResponse: briefData.aiResponse || '',
                                  manualBriefSections: briefData.manualBriefSections || {},
                                  mode: briefData.mode || 'ai'
                                },
                                selectedTrendingVideo: selectedTrendingVideo || null,
                                fullBrief: {
                                  ...briefData,
                                  briefSettings: effectiveBriefSettings
                                }
                              };
                              
                              // Generate script
                              let scriptResult;
                              try {
                                const { generateScript } = await import('../services/apiService');
                                const idToken = await user.getIdToken();
                                scriptResult = await generateScript(scriptGenerationData, idToken);
                              } catch (apiError) {
                                console.log('API call failed, using mock generation:', apiError);
                                scriptResult = await generateMockScript(scriptGenerationData);
                              }
                              
                              if (scriptResult && scriptResult.result) {
                                // Parse the result
                                let parsedLines = parseScriptContent(scriptResult.result);
                                if (scriptSettings.creativeControl === 'some') {
                                  const contextTips = buildContextTips(parsedLines, briefData);
                                  parsedLines = applyCreativeFlairInstructions({ parsedLines, contextTips, campaignGoal });
                                }
                                
                                // Replace the existing script in library (preserve ID, name, assignedBriefIds, and origin)
                                setScriptLibrary(prev => {
                                  const next = [...prev];
                                  next[activeScriptIdx] = {
                                    ...next[activeScriptIdx],
                                    lines: parsedLines,
                                    settings: scriptSettings,
                                    origin: 'ai', // Keep as AI
                                    updatedAt: new Date().toISOString()
                                  };
                                  return next;
                                });
                                
                                setStep(5); // Move to script editor
                              } else {
                                setError('No script generated');
                              }
                            } catch (err) {
                              console.error('Script regeneration error:', err);
                              setError('Failed to regenerate script: ' + err.message);
                            } finally {
                              setIsLoading(false);
                            }
                          }}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Regenerating...' : 'Regenerate with updated settings'}
                        </button>
                      )}
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          // Update script settings in library before going to editor
                          setScriptLibrary(prev => {
                            const next = [...prev];
                            next[activeScriptIdx] = {
                              ...next[activeScriptIdx],
                              settings: scriptSettings,
                              // IMPORTANT: preserve origin when updating settings
                              origin: next[activeScriptIdx].origin
                            };
                            return next;
                          });
                          setStep(5);
                        }}
                      >
                        {isAiScript ? 'View Script →' : 'Save Settings & Edit Script →'}
                      </button>
                    </>
                  );
                } else {
                  // No script yet - show Create/Generate button
                  const buttonText = mode === 'ai' ? 'Generate Script' : 'Create Script';
                  return (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        if (mode === 'ai') {
                          setIsCreatingNewScript(false);
                          handleCreateScriptWithAI();
                        } else {
                          handleCreateManualScript();
                        }
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Generating Script...' : buttonText}
                    </button>
                  );
                }
              })()}
            </div>
          </div>
        )}

        {/* Step 5: Script Editor */}
        {step === 5 && (
          <div className="step-content script-editor-step">
            <div ref={responseTopRef} />
            <div className="step-header">
              <h2>{mode === 'manual' ? 'Create Your Script' : 'Your Generated Script'}</h2>
              <p>
                {mode === 'manual'
                  ? 'Draft every section below, then refine or regenerate ideas as needed before saving.'
                  : 'Review and edit each section below. You can regenerate individual sections or save the complete script.'}
              </p>
            </div>
            
            <div className="script-response-container">
              <div className="script-sections">
                {console.log('Rendering script editor - scriptLibrary:', scriptLibrary, 'activeScriptIdx:', activeScriptIdx, 'current script:', scriptLibrary[activeScriptIdx])}
                {console.log('Script lines:', scriptLibrary[activeScriptIdx]?.lines)}
                {scriptLibrary[activeScriptIdx]?.lines && scriptLibrary[activeScriptIdx].lines.length > 0 ? (
                  scriptLibrary[activeScriptIdx].lines.map((line, index) => {
                  const sectionName = line.type === 'hook' ? 'Hook' : 
                                    line.type === 'line' ? 'Body Line' : 'CTA';
                  const sectionNumber = index + 1; // Dynamic numbering based on position
                  
                  // Calculate timing badge for body lines
                  let timingBadge = null;
                  let wordRange = null;
                  if (line.type === 'line') {
                    const duration = parseInt(scriptSettings.videoLength) || 30;
                    const bodyLines = scriptLibrary[activeScriptIdx].lines.filter(l => l.type === 'line').length;
                    const perBodyTime = getPerBodyTime(duration, bodyLines);
                    wordRange = getPerLineWordRange(perBodyTime);
                    timingBadge = `~${perBodyTime}s / ~${wordRange.min}–${wordRange.max} words`;
                  }
                  
                  // Count words in current script text
                  const wordCount = countWords(line.text || '');
                  
                  return (
                    <div key={index}>
                      <div className={`script-section ${line.type === 'hook' ? 'hook-section' : line.type === 'cta' ? 'cta-section' : ''}`}>
                        <div className="script-section-header">
                          <div className="script-section-title">
                            <h4>{sectionName}</h4>
                            {timingBadge && (
                              <span className="timing-badge">{timingBadge}</span>
                            )}
                          </div>
                          <div className="script-section-actions">
                            {/* Hook Rating button - only for manual mode and hook sections */}
                            {mode === 'manual' && line.type === 'hook' && (
                              <button
                                className="rate-hook-btn"
                                onClick={() => handleRateHook(index)}
                                disabled={isLoading || ratingHookIndex === index || regeneratingLineIndex === index || !line.text.trim()}
                              >
                                {ratingHookIndex === index ? (
                                  <>
                                    <span className="loading-spinner small"></span>
                                    Rating...
                                  </>
                                ) : (
                                  <>
                                    <Star size={16} />
                                    Rate Hook
                                  </>
                                )}
                              </button>
                            )}
                            {/* Generate Hook button - only for hook sections */}
                            {line.type === 'hook' && (
                              <button
                                className="regenerate-btn"
                                onClick={() => handleGenerateHook(index)}
                                disabled={ratingHookIndex === index || regeneratingLineIndex === index}
                              >
                                {regeneratingLineIndex === index && regeneratingLineType === 'hook' ? (
                                  <>
                                    <span className="loading-spinner small"></span>
                                    {mode === 'manual' ? 'Generating...' : 'Regenerating...'}
                                  </>
                                ) : (
                                  <>
                                    <span className="regenerate-icon">✨</span>
                                    {mode === 'manual' ? 'Generate Hook' : 'Regenerate Hook'}
                                  </>
                                )}
                              </button>
                            )}
                            {/* Enhance/regenerate button - for body lines and CTAs */}
                            {(line.type === 'line' || line.type === 'cta') && (
                              <button
                                className="regenerate-btn"
                                onClick={() => handleEnhanceContent(index)}
                                disabled={regeneratingLineIndex === index}
                              >
                                {regeneratingLineIndex === index && regeneratingLineType === line.type ? (
                                  <>
                                    <span className="loading-spinner small"></span>
                                    {mode === 'manual' ? 'Enhancing...' : 'Regenerating...'}
                                  </>
                                ) : (
                                  <>
                                    <span className="regenerate-icon">🚀</span>
                                    {mode === 'manual' ? 'AI Enhance' : 'Regenerate Section'}
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="script-section-content">
                          <div className="script-field">
                            <div className="field-label-row">
                              <label>Script</label>
                              {line.type === 'line' && (
                                <span className={`word-count ${wordCount > wordRange?.max ? 'over-limit' : ''}`}>
                                  {wordCount} words
                                </span>
                              )}
                            </div>
                            <textarea
                              value={line.text || ''}
                              onChange={(e) => handleScriptLineChange(index, 'text', e.target.value)}
                              className="script-content"
                              rows={4}
                            placeholder={isManualCreativeFlair ? 'Edit the script guidelines...' : 'Edit the script content...'}
                            />
                          </div>
                          
                          <div className="script-field">
                            <label>Action</label>
                            <input
                              type="text"
                              value={line.action || ''}
                              onChange={(e) => handleScriptLineChange(index, 'action', e.target.value)}
                              className="script-content"
                              placeholder="What should happen during this part?"
                            />
                          </div>
                          
                        <div className="script-field">
                          <label>Notes</label>
                          <textarea
                            value={line.notes || ''}
                            onChange={(e) => handleScriptLineChange(index, 'notes', e.target.value)}
                            className="script-content"
                            rows={3}
                            placeholder="Additional guidelines or notes..."
                          />
                        </div>


                        {(() => {
                          const currentHooks = scriptLibrary[activeScriptIdx].lines.filter(l => l.type === 'hook').length;
                          const currentCTAs = scriptLibrary[activeScriptIdx].lines.filter(l => l.type === 'cta').length;
                          const canDelete =
                            (line.type !== 'hook' || currentHooks > 1) &&
                            (line.type !== 'cta' || currentCTAs > 1) &&
                            scriptLibrary[activeScriptIdx].lines.length > 1;
                          return canDelete ? (
                          <button
                            className="remove-section-btn full-width"
                            onClick={() => handleRemoveScriptLine(index)}
                          >
                            Delete This {sectionName}
                          </button>
                          ) : null;
                        })()}
                        </div>
                        
                        {/* Display rating results - only for manual mode and hook sections */}
                        {mode === 'manual' && line.type === 'hook' && hookRatings[index] && (
                          <div className="hook-rating-results">
                            <div className="rating-score">
                              <div className="score-circle">
                                <span className="score-number">{hookRatings[index].score}</span>
                                <span className="score-total">/10</span>
                              </div>
                              <div className="score-explanation">
                                {hookRatings[index].explanation}
                              </div>
                            </div>
                            
                            {hookRatings[index].suggestions?.length > 0 && (
                              <div className="rating-suggestions">
                                <h6><Lightbulb size={16} /> Suggestions</h6>
                                <ul>
                                  {hookRatings[index].suggestions.map((suggestion, idx) => (
                                    <li key={idx}>{suggestion}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {hookRatings[index].improved_example && hookRatings[index].improved_example.trim() !== '' && (
                              <div className="improved-example">
                                <h6>Improved Example</h6>
                                <div className="example-text">
                                  "{hookRatings[index].improved_example}"
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Add Body Line button after each body line */}
                      {line.type === 'line' && (
                        <div className="add-body-line-container">
                          <button
                            className="add-body-line-btn"
                            onClick={handleAddScriptLine}
                          >
                            <span className="add-icon">+</span>
                            <span>Add Another Body Line</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                  })
                ) : (
                  <div className="no-script-message">
                    <p>No script content available. Please try generating a script again.</p>
                  </div>
                )}
              </div>
              
              <div className="script-actions">
                <div className="script-panel-action-buttons">
                  <button 
                    className="btn btn-secondary back-btn" 
                    onClick={() => setStep(4)}
                  >
                    ← Back to Settings
                  </button>
                  <button 
                    className="btn btn-secondary script-panel-draft-btn" 
                    onClick={() => handleSaveScript(true)} 
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="loading-spinner"></span>
                        Saving Draft...
                      </>
                    ) : (
                      'Save Draft'
                    )}
                  </button>
                  <button 
                    className="btn btn-primary script-panel-save-btn" 
                    onClick={() => handleSaveScript(false)} 
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="loading-spinner"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Script'
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {error && <div className="error-message">{error}</div>}
          </div>
        )}

        {showSuccess && (
          <div className="save-success-toast">
            {successMessage}
          </div>
        )}

        {/* Trending Videos Modal */}
        {showTrendingModal && (
          <div className="script-panel-trends-modal-overlay" onClick={() => setShowTrendingModal(false)}>
            <div className="script-panel-trends-modal" onClick={e => e.stopPropagation()}>
              <button 
                className="script-panel-trends-modal-close"
                onClick={() => setShowTrendingModal(false)}
              >
                ×
              </button>
              <h3>Trending Styles</h3>
              
              {loadingTrendingVideos ? (
                <div className="script-panel-trends-loading">
                  <div className="script-panel-trends-spinner"></div>
                  <p>Loading this week's trending styles...</p>
                </div>
              ) : trendingVideos.length === 0 ? (
                <div className="script-panel-trends-empty">
                  <p>No trending videos available this week. Check back soon!</p>
                </div>
              ) : (
                <div className="script-panel-trends-list">
                  {trendingVideos.map((video) => (
                    <div key={video.id} className="script-panel-trends-video">
                      <div className="script-panel-video-wrapper">
                        <video 
                          controls
                          loop 
                          playsInline
                          preload="metadata"
                        >
                          <source src={video.videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="script-panel-trends-title">{video.title}</div>
                      <div className="script-panel-trends-desc">{video.description}</div>
                      <button 
                        className="script-panel-trends-use-btn"
                        onClick={async () => {
                          setSelectedTrendingVideo(video);
                          setAiScriptStyle('trend');
                          setShowTrendingModal(false);
                          
                          // Trigger script generation with selected video
                          // This will be handled by the "Continue" button in step 4
                        }}
                      >
                        Use This Style
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 